/**
 * S-20 — Education wire schemas (Learning :5600, Exam :5700, Engagement :5800).
 *
 * Zod schemas mirror the BACKEND DTOs field-by-field (CourseSummaryDto,
 * ModuleDto, LessonDto, EnrollmentDto, ExamSummaryDto, EventSummaryDto, …).
 * Responses are parsed at the client seam so a contract drift fails LOUDLY at
 * the boundary instead of as a silent `undefined` deep in the UI (the auth
 * launch bug). Dates travel as ISO strings; the envelope is unwrapped by
 * createApiClient, so these schemas describe the `data` payload only.
 */
import { z } from 'zod';

// TCH-XLSX: the bulk-register row report rides in a ProblemDetails EXTENSION, so the readers below need
// the error shape. Type-only import — no runtime coupling between the two modules.
import type { ProblemDetails } from './envelope';

/** ISO-8601 timestamp as serialized by System.Text.Json */
const isoDate = z.string().min(1);

/** paged list payload [STD-API-8]: { items, totalItems } */
export function pagedListSchema<T extends z.ZodType>(item: T) {
  // Client zarfi acar: liste endpointlerinde data = DUZ DIZI, sayfalama meta.pagination
  // icindedir [STD-API-2]. Eski {items,totalItems} sekli de kabul edilir (2026-07-06).
  return z.union([
    z.object({ items: z.array(item), totalItems: z.number() }),
    z.array(item).transform((items) => ({ items, totalItems: items.length }))
  ]);
}

// ==============================|| LEARNING (:5600) ||============================== //

export const courseStatuses = ['draft', 'published', 'archived'] as const;
export const lessonContentTypes = ['video', 'text', 'quiz_ref', 'file'] as const;
export const enrollmentStatuses = ['active', 'completed', 'dropped'] as const;
export type EnrollmentStatus = (typeof enrollmentStatuses)[number];

/** CourseSummaryDto — C-1: locale = başlığın çözüldüğü dil (yoksa tr/legacy). */
export const courseSummarySchema = z.object({
  id: z.guid(),
  title: z.string(),
  locale: z.string(),
  status: z.enum(courseStatuses),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});

/**
 * PublicCourseDto (C-3) — a published course on the anonymous marketing catalog
 * (GET /api/v1/learning/public/courses). Localized title + description; no status
 * (public = always published) and no editor timestamps beyond updatedAtUtc.
 */
export const publicCourseSchema = z.object({
  id: z.guid(),
  title: z.string(),
  description: z.string().nullish(),
  locale: z.string(),
  updatedAtUtc: isoDate
});
export const publicCourseListSchema = z.array(publicCourseSchema);
export type PublicCourse = z.infer<typeof publicCourseSchema>;

/** CourseDetailDto — C-1: locale = içeriğin çözüldüğü dil. */
export const courseDetailSchema = z.object({
  id: z.guid(),
  title: z.string(),
  description: z.string().nullish(),
  locale: z.string(),
  status: z.enum(courseStatuses),
  moduleCount: z.number().int(),
  lessonCount: z.number().int(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});

/** CourseTranslationDto — C-1 çeviri editörü (draft dahil). */
export const courseTranslationSchema = z.object({
  locale: z.string(),
  title: z.string(),
  description: z.string().nullish(),
  status: z.enum(['draft', 'published']),
  updatedAtUtc: isoDate
});

/** ModuleDto */
export const moduleSchema = z.object({
  id: z.guid(),
  courseId: z.guid(),
  title: z.string(),
  sortOrder: z.number().int(),
  lessonCount: z.number().int(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});

/** LessonDto */
export const lessonSchema = z.object({
  id: z.guid(),
  courseId: z.guid(),
  moduleId: z.guid(),
  title: z.string(),
  contentType: z.enum(lessonContentTypes),
  contentRef: z.string().nullish(),
  body: z.string().nullish(),
  sortOrder: z.number().int(),
  durationMinutes: z.number().int(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});

/** EnrollmentDto — UserId comes from the JWT server-side, never sent by the client */
export const enrollmentSchema = z.object({
  id: z.guid(),
  courseId: z.guid(),
  userId: z.guid(),
  status: z.enum(enrollmentStatuses),
  progressPct: z.number(),
  enrolledAtUtc: isoDate,
  completedAtUtc: isoDate.nullish(),
  droppedAtUtc: isoDate.nullish()
});

/** LessonCompletionResultDto — fresh progress snapshot after a complete call */
export const lessonCompletionResultSchema = z.object({
  enrollmentId: z.guid(),
  lessonId: z.guid(),
  alreadyCompleted: z.boolean(),
  completedLessons: z.number().int(),
  totalLessons: z.number().int(),
  progressPct: z.number(),
  enrollmentStatus: z.enum(enrollmentStatuses)
});

// P5 teacher model — kursa atanmış eğitmen. userRef OPAK (identity.users.id).
/** CoursePrerequisiteDto — a prerequisite course (with its title/status) (P5-LD). */
export const coursePrerequisiteSchema = z.object({
  prerequisiteCourseId: z.guid(),
  title: z.string(),
  status: z.string(),
  createdAtUtc: isoDate
});
export type CoursePrerequisite = z.infer<typeof coursePrerequisiteSchema>;

export const instructorRoles = ['instructor', 'assistant'] as const;

/** InstructorDto — GET /learning/courses/{id}/instructors */
export const instructorSchema = z.object({
  userRef: z.guid(),
  role: z.enum(instructorRoles),
  assignedAt: isoDate
});

/** MyCourseDto — GET /learning/courses/mine (teacher-scoped; Faz 6 teacher SPA) */
export const myCourseSchema = z.object({
  courseId: z.guid(),
  title: z.string(),
  status: z.enum(courseStatuses),
  role: z.enum(instructorRoles)
});

export const courseListSchema = pagedListSchema(courseSummarySchema);
export const moduleListSchema = pagedListSchema(moduleSchema);
export const lessonListSchema = pagedListSchema(lessonSchema);
export const enrollmentListSchema = pagedListSchema(enrollmentSchema);
export const myCourseListSchema = pagedListSchema(myCourseSchema);

export type CourseSummary = z.infer<typeof courseSummarySchema>;
export type CourseDetail = z.infer<typeof courseDetailSchema>;
export type CourseTranslation = z.infer<typeof courseTranslationSchema>;
export type CourseModule = z.infer<typeof moduleSchema>;
export type Lesson = z.infer<typeof lessonSchema>;
export type Enrollment = z.infer<typeof enrollmentSchema>;
export type LessonCompletionResult = z.infer<typeof lessonCompletionResultSchema>;
export type Instructor = z.infer<typeof instructorSchema>;
export type MyCourse = z.infer<typeof myCourseSchema>;

/** CreateCourseCommand body */
export interface CreateCourseRequest {
  title: string;
  description?: string | null;
}

/** CreateLessonCommand body (moduleId travels in the URL) */
export interface CreateLessonRequest {
  title: string;
  contentType: (typeof lessonContentTypes)[number];
  contentRef?: string | null;
  body?: string | null;
  durationMinutes: number;
}

// ---- Homework / assignments (Phase B B-Edu; migration 123) ----
export const assignmentStatuses = ['draft', 'published', 'closed'] as const;
export const submissionStatuses = ['submitted', 'graded', 'returned'] as const;

/** AssignmentDto — a course's homework assignment. */
export const assignmentSchema = z.object({
  id: z.guid(),
  courseId: z.guid(),
  title: z.string(),
  instructions: z.string().nullish(),
  maxScore: z.number(),
  dueAtUtc: isoDate.nullish(),
  status: z.enum(assignmentStatuses),
  publishedAtUtc: isoDate.nullish(),
  createdAtUtc: isoDate
});

/** SubmissionDto — a student's submission. studentRef/attachmentRef are opaque [STD-OWN]. */
export const submissionSchema = z.object({
  id: z.guid(),
  assignmentId: z.guid(),
  studentRef: z.guid(),
  content: z.string().nullish(),
  attachmentRef: z.string().nullish(),
  status: z.enum(submissionStatuses),
  score: z.number().nullish(),
  feedback: z.string().nullish(),
  gradedAtUtc: isoDate.nullish(),
  submittedAtUtc: isoDate
});

export const assignmentListSchema = pagedListSchema(assignmentSchema);
export const submissionListSchema = pagedListSchema(submissionSchema);

/** AttachmentUploadResultDto — POST /assignments/{id}/attachment cevabı; attachmentRef opak (A-4b) [STD-OWN]. */
export const attachmentUploadResultSchema = z.object({
  attachmentRef: z.string().min(1),
  fileName: z.string().min(1),
  sizeBytes: z.number(),
  contentType: z.string().min(1)
});

export type Assignment = z.infer<typeof assignmentSchema>;
export type Submission = z.infer<typeof submissionSchema>;
export type AttachmentUploadResult = z.infer<typeof attachmentUploadResultSchema>;
export type AssignmentStatus = (typeof assignmentStatuses)[number];

/** CreateAssignmentCommand body */
export interface CreateAssignmentRequest {
  courseId: string;
  title: string;
  instructions?: string | null;
  maxScore: number;
  dueAt?: string | null;
}

/** SubmitAssignmentCommand body (assignmentId travels in the URL) */
export interface SubmitAssignmentRequest {
  content?: string | null;
  attachmentRef?: string | null;
}

/** GradeSubmissionCommand body (submissionId travels in the URL) */
export interface GradeSubmissionRequest {
  score: number;
  feedback?: string | null;
}

/**
 * RecommendationDto — GET /learning/recommendations?limit= (FE-9.3 "Sana önerilen").
 * Data is a plain array (not paged); score is a 0..1 relevance weight, reason is a short
 * server-resolved label the UI shows as a chip. courseId links to the catalog detail route.
 */
export const recommendationSchema = z.object({
  courseId: z.guid(),
  title: z.string(),
  score: z.number(),
  reason: z.string()
});
export const recommendationListSchema = z.array(recommendationSchema);
export type Recommendation = z.infer<typeof recommendationSchema>;

// ==============================|| EXAM (:5700) ||============================== //

export const examStatuses = ['draft', 'published', 'closed'] as const;

/** ExamSummaryDto */
export const examSummarySchema = z.object({
  id: z.guid(),
  title: z.string(),
  status: z.string(),
  durationMinutes: z.number().int(),
  passScore: z.number(),
  courseRef: z.guid().nullish(),
  opensAtUtc: isoDate.nullish(),
  closesAtUtc: isoDate.nullish(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});

/** ExamDetailDto */
export const examDetailSchema = z.object({
  id: z.guid(),
  title: z.string(),
  description: z.string().nullish(),
  status: z.string(),
  durationMinutes: z.number().int(),
  passScore: z.number(),
  courseRef: z.guid().nullish(),
  opensAtUtc: isoDate.nullish(),
  closesAtUtc: isoDate.nullish(),
  publishedAtUtc: isoDate.nullish(),
  closedAtUtc: isoDate.nullish(),
  questionCount: z.number().int(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});

export const examListSchema = pagedListSchema(examSummarySchema);

export type ExamSummary = z.infer<typeof examSummarySchema>;
export type ExamDetail = z.infer<typeof examDetailSchema>;

// ---- Question banks + questions (S-18 + P5-EX-1 ordering/multi_select) ----
export const questionTypes = ['multiple_choice', 'true_false', 'open_ended', 'ordering', 'multi_select'] as const;

/** BankDto */
export const questionBankSchema = z.object({
  id: z.guid(),
  name: z.string(),
  description: z.string().nullish(),
  questionCount: z.number().int(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});

export const questionOptionSchema = z.object({ key: z.string(), text: z.string() });

/**
 * QuestionDto. answerKey = single key (multiple_choice) or 'true'|'false'; answerKeys =
 * ordered sequence (ordering) / correct set (multi_select); open_ended has neither.
 */
export const questionSchema = z.object({
  id: z.guid(),
  bankId: z.guid(),
  type: z.enum(questionTypes),
  text: z.string(),
  options: z.array(questionOptionSchema).nullish(),
  answerKey: z.string().nullish(),
  answerKeys: z.array(z.string()).nullish(),
  points: z.number().int(),
  rubric: z.string().nullish(), // P5-EX-4: open_ended grading rubric (AI-suggestion source)
  aiGenerated: z.boolean(),
  approvedBy: z.guid().nullish(),
  approvedAtUtc: isoDate.nullish(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});

export const questionBankListSchema = pagedListSchema(questionBankSchema);
export const questionListSchema = pagedListSchema(questionSchema);

export type QuestionBank = z.infer<typeof questionBankSchema>;
export type ExamQuestion = z.infer<typeof questionSchema>;
export type QuestionOption = z.infer<typeof questionOptionSchema>;
export type QuestionType = (typeof questionTypes)[number];

// ---- Attempt grading (P5-EX-4: AI essay-grade suggestion + manual grading) ----
export const answerGradeStatuses = ['pending', 'pending_manual', 'graded'] as const;

/** AttemptDto (fields the grading UI needs). */
export const attemptSchema = z.object({
  id: z.guid(),
  examId: z.guid(),
  userId: z.guid(),
  status: z.string(),
  startedAtUtc: isoDate,
  deadlineAtUtc: isoDate,
  submittedAtUtc: isoDate.nullish(),
  gradedAtUtc: isoDate.nullish(),
  scorePct: z.number().nullish(),
  passed: z.boolean().nullish(),
  // G1 (mig 167) → FE-4: does this attempt's exam require proctoring? The student never carries
  // exam.exams.read, so the proctoring session lifecycle is keyed off the attempt itself. Optional
  // + default(false) keeps every existing attempt payload (grading queue/detail) parseable.
  requiresProctoring: z.boolean().optional().default(false)
});

/** GradingQueueItemDto — a submitted attempt awaiting manual grading. */
export const gradingQueueItemSchema = z.object({
  attemptId: z.guid(),
  examId: z.guid(),
  userId: z.guid(),
  submittedAtUtc: isoDate,
  pendingManualCount: z.number().int()
});
export const gradingQueueListSchema = pagedListSchema(gradingQueueItemSchema);

/** GradingAnswerDto — grader view of one answer (text + rubric + advisory AI suggestion). */
export const gradingAnswerSchema = z.object({
  examQuestionId: z.guid(),
  type: z.string(),
  questionText: z.string(),
  rubric: z.string().nullish(),
  answerText: z.string(),
  gradeStatus: z.enum(answerGradeStatuses),
  awardedPoints: z.number().nullish(),
  points: z.number().int(),
  aiSuggestedCorrect: z.boolean().nullish(),
  aiRationale: z.string().nullish(),
  aiSuggestedAtUtc: isoDate.nullish()
});

export const gradingAttemptDetailSchema = z.object({
  attempt: attemptSchema,
  answers: z.array(gradingAnswerSchema)
});

/** EssayGradeSuggestionDto — result of POST .../answers/{eqId}/suggest-grade [STD-AI]. */
export const essayGradeSuggestionSchema = z.object({
  examQuestionId: z.guid(),
  suggestedCorrect: z.boolean(),
  rationale: z.string(),
  suggestedAtUtc: isoDate
});

/** AttemptGradingResultDto — result of manual grade (attempt + remaining pending count). */
export const attemptGradingResultSchema = z.object({
  attempt: attemptSchema,
  pendingManualCount: z.number().int()
});

// ---- Student exam-taking (P6): answer-key-SAFE question + attempt detail/answer ----
/** ExamQuestionDto (student view) — NO answer key; options present for choice/order types. */
export const examTakingQuestionSchema = z.object({
  id: z.guid(),
  questionId: z.guid(),
  sortOrder: z.number().int(),
  type: z.enum(questionTypes),
  text: z.string(),
  options: z.array(questionOptionSchema).nullish(),
  points: z.number().int()
});
/** ExamQuestionListResult — { items } (not paged). */
export const examTakingQuestionListSchema = z.object({ items: z.array(examTakingQuestionSchema) });

/** AttemptAnswerDto — one answer's grade state (+ advisory AI fields from P5-EX-4). */
export const attemptAnswerSchema = z.object({
  examQuestionId: z.guid(),
  gradeStatus: z.enum(answerGradeStatuses),
  awardedPoints: z.number().nullish(),
  answeredAtUtc: isoDate,
  aiSuggestedCorrect: z.boolean().nullish(),
  aiRationale: z.string().nullish(),
  aiSuggestedAtUtc: isoDate.nullish(),
  // BE-3.2 (J3) → FE-8d: grader's free-text feedback for this answer. Student sees it on the
  // result screen only when present (null when the grader left none).
  feedback: z.string().nullish()
});

/** AttemptDetailDto — attempt + per-answer status (student's own attempt). */
export const attemptDetailSchema = z.object({
  attempt: attemptSchema,
  answers: z.array(attemptAnswerSchema)
});

export type ExamTakingQuestion = z.infer<typeof examTakingQuestionSchema>;
export type AttemptAnswer = z.infer<typeof attemptAnswerSchema>;
export type AttemptDetail = z.infer<typeof attemptDetailSchema>;

/** ItemStatDto — per-question classical item stats (difficulty p, discrimination D). */
export const itemStatSchema = z.object({
  examQuestionId: z.guid(),
  sortOrder: z.number().int(),
  responseCount: z.number().int(),
  correctCount: z.number().int(),
  difficultyPValue: z.number(),
  discriminationIndex: z.number().nullable()
});

/** ExamItemAnalysisDto — GET /exam/grading/exams/{examId}/item-analysis (grader psychometrics). */
export const examItemAnalysisSchema = z.object({
  examId: z.guid(),
  attemptsAnalyzed: z.number().int(),
  items: z.array(itemStatSchema)
});

export type Attempt = z.infer<typeof attemptSchema>;
export type GradingQueueItem = z.infer<typeof gradingQueueItemSchema>;
export type GradingAnswer = z.infer<typeof gradingAnswerSchema>;
export type GradingAttemptDetail = z.infer<typeof gradingAttemptDetailSchema>;
export type ItemStat = z.infer<typeof itemStatSchema>;
export type ExamItemAnalysis = z.infer<typeof examItemAnalysisSchema>;
export type EssayGradeSuggestion = z.infer<typeof essayGradeSuggestionSchema>;
export type AttemptGradingResult = z.infer<typeof attemptGradingResultSchema>;

// ---- Proctoring (A-2; RecordOnly pilot) — grader review surface -----------------------------
// ProctoringSessionDto / ProctoringViolationDto / ProctoringSessionDetailDto (ProctoringFeatures.cs).
// The grader read is GET /api/v1/exam/grading/attempts/{attemptId}/proctoring (exam.attempts.grade):
// one session per attempt + its violations (fraud flags). Vision/enforcement is E-7 [STD-DRIFT].
export const proctoringSessionStatuses = ['active', 'ended'] as const;
export const proctoringSeverities = ['info', 'warning', 'critical'] as const;

/** ProctoringSessionDto — session summary for one attempt. */
export const proctoringSessionSchema = z.object({
  attemptId: z.guid(),
  status: z.string(),
  provider: z.string(),
  startedAtUtc: isoDate,
  lastHeartbeatAtUtc: isoDate.nullish(),
  endedAtUtc: isoDate.nullish(),
  violationCount: z.number().int()
});

/** ProctoringViolationDto — one recorded violation (a "fraud flag"). action is empty on the grader read. */
export const proctoringViolationSchema = z.object({
  id: z.guid(),
  type: z.string(),
  severity: z.string(),
  detail: z.string().nullish(),
  occurredAtUtc: isoDate,
  action: z.string()
});

/** ProctoringSessionDetailDto — grader review (session + violations, newest first). */
export const proctoringSessionDetailSchema = z.object({
  session: proctoringSessionSchema,
  violations: z.array(proctoringViolationSchema)
});

export type ProctoringSession = z.infer<typeof proctoringSessionSchema>;
export type ProctoringViolation = z.infer<typeof proctoringViolationSchema>;
export type ProctoringSessionDetail = z.infer<typeof proctoringSessionDetailSchema>;
export type ProctoringSeverity = (typeof proctoringSeverities)[number];

/**
 * FE-4 (student write): body for POST /api/v1/exam/attempts/{id}/proctoring/violations
 * (ReportViolationRequest). The Idempotency-Key is applied automatically by createApiClient on
 * the write — do NOT put it in the body [STD-API-4]. RecordOnly pilot: the provider Noops the
 * decision; the violation is still recorded on the session (vision/enforcement is E-7) [STD-DRIFT].
 */
export interface ReportViolationRequest {
  type: string;
  severity: ProctoringSeverity;
  detail?: string | null;
}

/** CreateExamCommand body */
export interface CreateExamRequest {
  title: string;
  description?: string | null;
  durationMinutes: number;
  passScore: number;
  courseRef?: string | null;
  opensAtUtc?: string | null;
  closesAtUtc?: string | null;
}

// ==============================|| ENGAGEMENT (:5800) ||============================== //

// DG-3 (mig 205): eventType/languageCode ARTIK OPAK definition ID (guid) — eski opak kodun yerini aldı.
// Değerler Definitions servisinin 'event_types'/'languages' gruplarından gelir; FE id'ye göre saklar ve
// id→ad çözer (definition.id) [STD-OWN]. Tümü nullish (backend Guid?).
/**
 * EVT-MODE (mig 308) — how much an event wants one information block. Mirrors EventInfoModes and the
 * ck_events_*_mode CHECKs.
 *
 * THREE STATES, not a boolean: "collect it" and "insist on it" are different decisions. 'required'
 * BLOCKS NOTHING — the tabs only exist after registration, so it means "staff can see who is missing".
 */
export const eventInfoModes = ['off', 'optional', 'required'] as const;
export type EventInfoMode = (typeof eventInfoModes)[number];

/** The single question a tab asks: is this block collected on this event at all? */
export function collectsInfo(mode: string | null | undefined): boolean {
  // Fail CLOSED on anything unrecognised, matching the server's EventInfoModes.Collects — an unknown
  // mode must not behave like 'optional' and show a tab the admin never enabled.
  return mode === 'optional' || mode === 'required';
}

export const eventStatuses = ['draft', 'published', 'archived'] as const;

/** EventSummaryDto — EV alanları (DG-3 mig 205) forward/back-uyumlu (nullish). */
/**
 * REG-2AX (mig 439) — the two coarse statuses the server now computes, in the order a person asks
 * them: "has any money arrived?" and "is this person in?".
 *
 * ⚠ THE SERVER OWNS THE RULE. Both are `GENERATED ALWAYS AS … STORED` columns in the tenant DB, so the
 * client RENDERS them and must never re-derive them from `paymentState`/`amountPaid`. Re-deriving is
 * how the badge and the row it sits on end up disagreeing — a second definition of one truth, which is
 * the failure this pair was introduced to remove.
 *
 * ⚠ `registered` IS NOT ACCESS. The content gate is unchanged and still needs FULL payment
 * (`paymentState` in not_required|paid), so a part-paid registrant reads "Registered" next to content
 * that is still locked. Any screen showing this badge has to say so, or it recreates the contradiction
 * mig 406 closed from the other side.
 *
 * ⚠ AND `not_registered_yet` STILL HOLDS A SEAT. Its `status` is `awaiting_payment`, which is in
 * `seatHoldingRegistrationStatuses` and counts against capacity. Never treat the label as free
 * inventory.
 */
export const registrationPaymentStatuses = ['awaiting_payment', 'partially_paid', 'paid'] as const;
export const coarseRegistrationStatuses = ['not_registered_yet', 'registered'] as const;

/**
 * ⚠ NULLISH, NOT A REQUIRED ENUM, and for two independent reasons — either alone would blank a page.
 *
 *   1. The server returns NULL off the seat path (waitlisted / cancelled): the lifecycle is the answer
 *      there, and a strict enum would throw on the null.
 *   2. A server predating mig 439 does not send the fields at all, and cached responses hold the OLD
 *      DTO shape for up to the query TTL after a deploy.
 *
 * `.parse` failures are not local: one bad field throws for the whole document, so a required enum
 * here empties the entire roster rather than one cell.
 */
const coarsePaymentStatusField = z.enum(registrationPaymentStatuses).nullish();
const coarseRegistrationStatusField = z.enum(coarseRegistrationStatuses).nullish();

export const eventSummarySchema = z.object({
  id: z.guid(),
  title: z.string(),
  eventTypeDefinitionId: z.guid().nullish(), // opak definition id (event_types)
  programTypeId: z.guid().nullish(), // opak definition id (program_types), optional
  currency: z.string().default('TRY'), // 3-letter ISO 4217 for the event's price lists
  status: z.enum(eventStatuses),
  startsAtUtc: isoDate,
  endsAtUtc: isoDate,
  location: z.string().nullish(),
  capacity: z.number().int().nullish(),
  displayZone: z.string().nullish(),
  languageDefinitionId: z.guid().nullish(), // opak definition id (languages)
  passingRatio: z.number().nullish(),
  hasPricelist: z.boolean().optional().default(false),
  responsibleUserRef: z.guid().nullish(),
  organizerId: z.guid().nullish(), // opak company uuid (type='organizer')
  isOnline: z.boolean().optional().default(false),
  meetingUrl: z.string().nullish(),
  // Caller's own registration state on this event (null = not registered). Lets the list render the
  // right chip/button on a fresh load without a session-local overlay.
  myRegistrationStatus: z.string().nullish(),
  // EVT-PAY: cagiranin kendi kaydinin kimligi + odeme durumu projeksiyonu. Ogrenci odeme adimini
  // bunlarla kurar (sayfa yenilendikten sonra da). Absent -> odeme gerekmiyor varsayilir; gercek
  // kapi SUNUCUDADIR, bu alan yalniz arayuzu yonlendirir.
  myRegistrationId: z.guid().nullish(),
  myPaymentState: z
    .enum(['not_required', 'awaiting_plan', 'plan_active', 'awaiting_approval', 'partially_paid', 'paid', 'defaulted'])
    .nullish()
    .transform((v) => v ?? 'not_required'),
  // REG-2AX (mig 439) — the caller's own coarse pair.
  //
  // ⚠ `myCoarseRegistrationStatus`, NOT `myRegistrationStatus`: that name is taken by the LIFECYCLE
  // (registered | awaiting_payment | waitlisted | cancelled | attended) which three SPAs read, and the
  // server field is named the same way for the same reason.
  myPaymentStatus: coarsePaymentStatusField,
  myCoarseRegistrationStatus: coarseRegistrationStatusField,
  // CNT-PCT (mig 458): içerik AÇILDIYSA damgalanır — tahsil edilen para etkinliğin
  // funding_min_percent'ine (varsayılan %20) ulaştığı an. Dolu = içerik sekmesi, DOSYALAR ve kendi
  // seyahat/vize kaydı okunabilir.
  //
  // ⚠ NULL "KAPALI" DEMEK DEĞİLDİR ve tek başına okunursa ücretsiz etkinlikleri yanlışlıkla kilitli
  // gösterir: sunucudaki kapı İKİ tanığı OR'lar ve eskisi (borç yok = not_required|paid) ZEMİN olarak
  // durur — ödenecek bir şey olmayan kayda damga hiç yazılmaz. İstemci de ikisini OR'lamalı.
  myContentUnlockedAt: isoDate.nullish(),
  myWaitlistPosition: z.number().int().nullish(),
  // EV-COVER (mig 240): kapak görseli var mı — byte'lar GET /events/{id}/cover'dan gelir (auth'lı blob).
  hasCoverImage: z.boolean().optional().default(false),
  // PRC (alıcı-çözümü v1): aktif dönemin VARSAYILAN bilet fiyatlarından min "başlangıç" tutarı (currency birimi); yoksa null.
  priceFrom: z.number().nullish(),
  // Etkinliğin ücretli bileti yok (saf RSVP) → ücretsiz.
  isFree: z.boolean().optional().default(false),
  // TCH-TKT/P2: teacher-purchased tickets that have not become a seat-holding registration yet ALSO
  // hold seats, so availability is NOT `capacity - registeredCount` any more. Both fields are computed
  // SERVER-side with the same EventCapacity.IsFull the register handler uses — do NOT re-derive
  // fullness in the FE: doing so is what showed "Register" on a full event and silently waitlisted the
  // student.
  reservedCount: z.number().int().nullish().transform((v) => v ?? 0),
  isFull: z.boolean().nullish().transform((v) => v ?? false),
  /** Seats left (capacity − seat-holding registrations − reservations); null = unlimited. Server-computed. */
  seatsRemaining: z.number().int().nullish(),
  // TCH-TKT: a ticket the caller's TEACHER bought and assigned to them, still unused (the list twin of
  // the detail field). Present => the student can register WITHOUT paying; the events grid shows an
  // "your teacher reserved a ticket" badge from it. The query is [NoCache] server-side because this
  // field is caller-specific.
  myAssignedTicketId: z.guid().nullish(),
  // TCH-INV (mig 292): a pending OPEN invitation for the caller — the kind that reserves NO seat.
  // Kept separate from myAssignedTicketId because the copy differs: "your ticket is ready" vs "you
  // were invited". Server-side [NoCache], like every caller-specific field here.
  myOpenInvitationId: z.guid().nullish()
});

/** EventDetailDto */
export const eventDetailSchema = z.object({
  id: z.guid(),
  title: z.string(),
  description: z.string().nullish(),
  eventTypeDefinitionId: z.guid().nullish(), // opak definition id (event_types) — DG-3 mig 205
  programTypeId: z.guid(), // opak definition id (program_types) — REQUIRED (always present)
  currency: z.string().default('TRY'), // 3-letter ISO 4217 for the event's price lists
  status: z.enum(eventStatuses),
  startsAtUtc: isoDate,
  endsAtUtc: isoDate,
  location: z.string().nullish(),
  capacity: z.number().int().nullish(),
  displayZone: z.string().nullish(),
  languageDefinitionId: z.guid().nullish(), // opak definition id (languages) — DG-3 mig 205
  passingRatio: z.number().nullish(),
  hasPricelist: z.boolean().optional().default(false),
  responsibleUserRef: z.guid().nullish(),
  organizerId: z.guid().nullish(), // opak company uuid (type='organizer')
  isOnline: z.boolean().optional().default(false),
  meetingUrl: z.string().nullish(),
  // DG-3: tags stay string[] but each element is now a definition ID (was a code); group `event_tags`.
  tags: z.array(z.string()).optional().default([]),
  // Audience filters (EV audience epic) — all OPTIONAL, empty array = unrestricted (open to everyone).
  // DG-3: countries/ageIntervals/genders values are now definition IDs (Definitions groups
  // `country`/`age_intervals`/`genders`); schools = company uuids (type='school', UNCHANGED).
  // Forward/back-compat (nullish→[]).
  countries: z.array(z.string()).nullish().transform((v) => v ?? []),
  schools: z.array(z.string()).nullish().transform((v) => v ?? []),
  ageIntervals: z.array(z.string()).nullish().transform((v) => v ?? []),
  genders: z.array(z.string()).nullish().transform((v) => v ?? []),
  // EV-AUD-EXCL (mig 487) — the EXCLUDE side of the same dimensions. Empty = nobody excluded.
  // A dimension carries EITHER list, never both: the server rejects both-non-empty with 422.
  // ⚠ nullish→[] is load-bearing here, not boilerplate: these keys are absent from every response
  // a pre-487 server sends, and a bare z.array() would throw and blank the whole page rather than
  // one field (see the nullable-column zod sweep).
  // There is no excludedAgeIntervals — the age_interval dimension is inert server-side.
  excludedCountries: z.array(z.string()).nullish().transform((v) => v ?? []),
  excludedSchools: z.array(z.string()).nullish().transform((v) => v ?? []),
  excludedGenders: z.array(z.string()).nullish().transform((v) => v ?? []),
  registeredCount: z.number().int(),
  waitlistedCount: z.number().int(), // #3 bekleme listesi
  // Caller's own registration state on this event (null = not registered).
  myRegistrationStatus: z.string().nullish(),
  // EVT-PAY: cagiranin kendi kaydinin kimligi + odeme durumu projeksiyonu. Ogrenci odeme adimini
  // bunlarla kurar (sayfa yenilendikten sonra da). Absent -> odeme gerekmiyor varsayilir; gercek
  // kapi SUNUCUDADIR, bu alan yalniz arayuzu yonlendirir.
  myRegistrationId: z.guid().nullish(),
  myPaymentState: z
    .enum(['not_required', 'awaiting_plan', 'plan_active', 'awaiting_approval', 'partially_paid', 'paid', 'defaulted'])
    .nullish()
    .transform((v) => v ?? 'not_required'),
  // REG-2AX (mig 439) — the caller's own coarse pair.
  //
  // ⚠ `myCoarseRegistrationStatus`, NOT `myRegistrationStatus`: that name is taken by the LIFECYCLE
  // (registered | awaiting_payment | waitlisted | cancelled | attended) which three SPAs read, and the
  // server field is named the same way for the same reason.
  myPaymentStatus: coarsePaymentStatusField,
  myCoarseRegistrationStatus: coarseRegistrationStatusField,
  // CNT-PCT (mig 458): içerik AÇILDIYSA damgalanır — tahsil edilen para etkinliğin
  // funding_min_percent'ine (varsayılan %20) ulaştığı an. Dolu = içerik sekmesi, DOSYALAR ve kendi
  // seyahat/vize kaydı okunabilir.
  //
  // ⚠ NULL "KAPALI" DEMEK DEĞİLDİR ve tek başına okunursa ücretsiz etkinlikleri yanlışlıkla kilitli
  // gösterir: sunucudaki kapı İKİ tanığı OR'lar ve eskisi (borç yok = not_required|paid) ZEMİN olarak
  // durur — ödenecek bir şey olmayan kayda damga hiç yazılmaz. İstemci de ikisini OR'lamalı.
  myContentUnlockedAt: isoDate.nullish(),
  // CNT-PCT: içeriği açmak için HÂLÂ ödenmesi gereken tutar — "materyalleri açmak için ₺X daha".
  // Açık kayıtta ve ücretsiz/biletsiz kayıtta null.
  //
  // ⚠ YALNIZ DETAYDA (özet şemada yok, bilerek): eşik etkinlik başına çözülür ve listede sayfa başına
  // N okuma demekti. Kart için "açık mı" yeter. Yüzdeyi İSTEMCİDE çözmeye kalkmayın — sunucu TUTAR
  // gönderir, çünkü etkinlik bazlı geçersiz kılmayı (mig 353) ikinci bir yerde türetmek olurdu.
  myPayToUnlockContent: z.number().nullish(),
  myWaitlistPosition: z.number().int().nullish(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate,
  // EV-COVER (mig 240): kapak görseli var mı — byte'lar GET /events/{id}/cover'dan gelir (auth'lı blob).
  hasCoverImage: z.boolean().optional().default(false),
  // PRC (alıcı-çözümü v1): aktif dönemin VARSAYILAN bilet fiyatlarından min "başlangıç" tutarı (currency birimi); yoksa null.
  priceFrom: z.number().nullish(),
  // Etkinliğin ücretli bileti yok (saf RSVP) → ücretsiz.
  isFree: z.boolean().optional().default(false),
  // CF (mig 252): the event has a consent form → the register flow MUST show it and collect consent
  // (the server 422s otherwise). Bytes come from GET /events/{id}/consent-form (authorized blob).
  hasConsentForm: z.boolean().optional().default(false),
  // Which revision is current; a consent records the revision it was given against.
  consentFormUploadedAtUtc: isoDate.nullish(),
  // EVT-PAY (mig 271): son taksit etkinlik baslangicindan en az bu kadar gun ONCE odenmis olmali.
  // NULL = servis varsayilani (7 gun) — form bos gosterir ve bos gonderirse varsayilan korunur.
  paymentDeadlineDays: z.number().int().nullish(),
  // FUND (mig 353): "ödendi seç" eşiği — biletin TAHSİL EDİLEN birim fiyatının yüzdesi.
  // NULL = platform varsayılanı (%20); form boş gösterir ve boş gönderirse varsayılan korunur.
  // ⚠ Boş ile 20 AYNI ŞEY DEĞİL: ikincisi "biri 20 seçti" demektir ve varsayılan değişirse takip etmez.
  fundingMinPercent: z.number().int().nullish(),
  // INST-SELL (mig 277): dahili "Genel" fiyat listesinin taksit anahtarı — Fiyat Listesi sekmesindeki
  // Genel satırı bunu gösterir (o listenin kendi satırı yoktur). false = yalnız tek çekim önerilir.
  // Optional+default: FE, alanı taşımayan bir BE'den önce yayına alınabilir ve "açık" güvenli varsayımdır
  // (kapalı varsaymak, taksidi hiç sunmayan bir UI gösterirdi).
  defaultInstallmentsEnabled: z.boolean().optional().default(true),
  // CUR (mig 286): SEÇİLEN para biriminin OPAK definition id'si (grup 'currencies'). `currency` ise
  // ÖDEME HATTININ kullandığı ISO-4217 KODUdur (ödenebilirlik olayı → commerce.orders → Stripe) —
  // ikisi birlikte yaşar, biri diğerinin yerini ALMAZ. Form seçimi bu id'den geri okur.
  // EVT-MODE (mig 308): how much this event wants each information block — 'off' | 'optional' |
  // 'required'. The student shell builds its tab list from these instead of a hardcoded array.
  //
  // NOT z.enum(): an older/newer server must not fail the parse over a value this build does not know.
  // Optional+default keeps the FE deployable ahead of the BE, and the defaults are the SAFE assumption —
  // they reproduce today's behaviour (both tabs shown, no passport expectation) rather than hiding a tab
  // because a field was missing.
  travelInfoMode: z.string().optional().default('optional'),
  visaInfoMode: z.string().optional().default('optional'),
  passportMode: z.string().optional().default('off'),
  currencyDefinitionId: z.guid().nullish(),
  // TCH-TKT (mig 281): öğretmenin ÇAĞIRANA atadığı, ÖDENMİŞ ve kullanılmamış bilet. Kayıt formu bunu
  // "hazır bilet" olarak gösterir ve ödeme adımını ATLAR. null/absent = davet yok.
  // Sunucu bu alanları YALNIZ atama assigned + batch paid iken doldurur; FE ayrıca kontrol etmez —
  // "bedava kayıt" sinyalinin tek kaynağı sunucudur.
  myAssignedTicketId: z.guid().nullish(),
  myAllocationId: z.guid().nullish(),
  // TCH-TKT/P2: teacher-purchased tickets that have not become a seat-holding registration yet ALSO
  // hold seats, so availability is NOT `capacity - registeredCount` any more. Both fields are computed
  // SERVER-side with the same EventCapacity.IsFull the register handler uses — do NOT re-derive
  // fullness in the FE: doing so is what showed "Register" on a full event and silently waitlisted the
  // student.
  reservedCount: z.number().int().nullish().transform((v) => v ?? 0),
  isFull: z.boolean().nullish().transform((v) => v ?? false),
  /** Seats left (capacity − seat-holding registrations − reservations); null = unlimited. Server-computed. */
  seatsRemaining: z.number().int().nullish(),
  // TKT-CAP (mig 288): the ticket TYPES' attendee limits must sum to at most the event capacity.
  // `ticketLimitsTotal` is that sum (unlimited ticket types count 0) and `ticketLimitsUnallocated`
  // is what is left to hand out (null = unlimited capacity ⇒ no rule). Both are server-computed by
  // the same summer the ticket endpoints validate against — the admin form shows them as the cap,
  // but the authority stays the 422 (`allowed`).
  ticketLimitsTotal: z.number().int().nullish().transform((v) => v ?? 0),
  ticketLimitsUnallocated: z.number().int().nullish(),
  /** TCH-INV (mig 292): a pending OPEN invitation for the caller (reserves no seat). */
  myOpenInvitationId: z.guid().nullish()
});

export const eventListSchema = pagedListSchema(eventSummarySchema);

// ------------------------------ EVENT COMMERCE (Engagement :5800) -------------------------------
// An event groups commerce products by product type, bundles them into tickets, and prices tickets per
// adopted Commerce pricing list. productTypeId is an OPAQUE definition id (group product_types);
// productRef is an OPAQUE commerce.products uuid (never resolved cross-schema [STD-OWN]). camelCase
// wire, Zod-parsed at the seam. Endpoints under /api/v1/engagement/events/{id}/...

/**
 * EventProductGroupDto — an event groups its products by product type. Each group is an explicit
 * product_types definition (productTypeId) holding attached products (productRef → commerce.products).
 * GET /events/{id}/product-groups returns a bare array (ordered by sortOrder).
 */
export const eventProductGroupSchema = z.object({
  id: z.guid(),
  productTypeId: z.guid(),
  sortOrder: z.number().int(),
  // A required group with ≥1 product must be satisfied by every ticket.
  isRequired: z.boolean().default(false),
  products: z.array(
    z.object({
      id: z.guid(), // eventProductId
      productRef: z.guid(),
      sortOrder: z.number().int(),
      /**
       * PRD-LBL (mig 298): attendee-facing labels, denormalized onto event_products at attach time.
       *
       * ⚠ THE STUDENT SPA HAS NO OTHER SOURCE FOR THESE. The product name lives in commerce.products
       * and `commerce.products.view` is not granted to the student role, so `productRef` is
       * unresolvable there — render `displayName`, never the ref. `''` means the label was never
       * captured (a pre-298 row the migration could not resolve); fall back to the group name.
       *
       * Defaulted so a pre-298 response still parses.
       */
      displayName: z.string().default(''),
      displayDescription: z.string().nullish(),
      features: z.array(z.string()).default([])
    })
  )
});
export const eventProductGroupListSchema = z.array(eventProductGroupSchema);

/** PRD-LBL: POST/PUT body for a product's attendee-facing labels. SET semantics — send all three. */
export interface EventProductLabelsRequest {
  displayName?: string | null;
  displayDescription?: string | null;
  features?: string[];
}

/**
 * PRD-ORD: body for all THREE reorder routes — `PUT /events/{id}/product-groups/order`,
 * `PUT /events/{id}/product-groups/{groupId}/products/order` and `PUT /events/{id}/tickets/order`.
 *
 * ⚠ FULL PERMUTATION, NOT A PARTIAL SORT. `orderedIds` must contain exactly the ids currently in the
 * set; anything missing/extra/duplicated is a **409**, on purpose — a row left out would keep its old
 * sort_order and land in the middle of the server's normalized 1..n. So send the whole list, and on
 * 409 refetch rather than retry (the client's list was stale).
 *
 * The two product routes respond with the affected list ALREADY in the new order. The TICKET route
 * deliberately does not: it returns `{id, name, sortOrder}` only, because the full ticket DTO carries
 * live availability that only the list query computes — refetch `/tickets` (it is uncached) instead.
 */
export interface ReorderEventCatalogRequest {
  orderedIds: string[];
}

/** PRD-ORD: what `PUT /events/{id}/tickets/order` echoes back — identity and the new order, nothing else. */
export interface EventTicketOrderRow {
  id: string;
  name: string;
  sortOrder: number;
}

/** PRD-LBL caps — mirror the server validators and ck_event_products_labels (mig 298). */
export const eventProductLabelLimits = {
  displayName: 200,
  displayDescription: 1000,
  features: 20,
  featureLength: 120
} as const;

/** PRD-LBL: one product row inside a group (the type alias for the group itself lives below). */
export type EventProduct = z.infer<typeof eventProductGroupSchema>['products'][number];

/**
 * EventTicketDto — a named bundle picking one product per (selected) group. GET /events/{id}/tickets
 * returns a bare array. A ticket must have a selection for every REQUIRED group that has ≥1 product;
 * optional (and required-but-empty) groups may be omitted. Writes send only {groupId, eventProductId}.
 */
export const eventTicketSchema = z.object({
  id: z.guid(),
  name: z.string(),
  /**
   * TAX (mig 383): KDV rate as a percentage (20 = %20).
   *
   * ⚠ The ticket PRICE is TAX-INCLUSIVE — this splits it, it does not add to it. Commerce never reads
   * this column; the rate rides the payable event and is frozen onto the order line, so editing it
   * changes only purchases made AFTERWARDS.
   *
   * `.default(0)` so a pre-383 server response still parses — a failing ticket schema blanks the whole
   * tickets tab, not one field.
   */
  taxRate: z.number().default(0),
  sortOrder: z.number().int(),
  // Capacity limits (null = unlimited; >= 1 when set).
  maxAttendees: z.number().int().nullish(),
  maxPerOrder: z.number().int().nullish(),
  /**
   * Seat-holding registrations for this ticket (confirmed, not waitlisted).
   *
   * ⚠ NOT AVAILABILITY — informational only (the admin's "28 / 40"). It does NOT count seats a
   * teacher has bought but not yet handed out, so `maxAttendees - soldCount` reports a free seat on
   * a ticket the server will silently WAITLIST. Use {@link eventTicketSchema.shape.seatsRemaining}
   * and `isFull` instead; they come from the same rule the register handler applies.
   */
  soldCount: z.number().int().default(0),
  /**
   * TKT-AVAIL: server-computed availability (registrations + reserved teacher allocations).
   * `seatsRemaining` is null when the ticket type is unlimited, and `isFull` is then always false.
   *
   * Both are nullish so a response from a server that predates TKT-AVAIL still parses — `isFull` then
   * reads false, which matches the old (optimistic) behaviour rather than hiding every ticket.
   * `.nullish().transform()` (not `.optional().default()`) to match the event schemas above and to
   * tolerate an explicit `isFull: null`, which a default alone would reject.
   */
  seatsRemaining: z.number().int().nullish(),
  isFull: z
    .boolean()
    .nullish()
    .transform((v) => v ?? false),
  /**
   * TKT-CODE (mig 299): short human handle shown beside the price ("KPD-ET"). A LABEL, never a key —
   * do not match, sort or look up by it; `id` is the identity. null/absent = no code.
   * Unique per event, case-insensitively, and the typed case is preserved for display.
   */
  code: z.string().nullish(),
  // TKT-DETAIL (mig 243): plain-text description + whether a PDF document exists (bytes at .../document).
  description: z.string().nullish(),
  hasDocument: z.boolean().optional().default(false),
  /**
   * TKT-ROLE (mig 332): the PARTICIPANT role codes this ticket is open to.
   *
   * ⚠ EMPTY MEANS EVERYONE, not nobody — mig 332 ships with no backfill, so every existing ticket
   * arrives with an empty array and must keep behaving as it does today. Render "anyone can see this"
   * for `[]`, never "no one".
   *
   * Values are `rbac.roles.code`, the same strings the JWT carries. Defaults to `[]` so a response
   * from a server predating TKT-ROLE still parses as unrestricted.
   *
   * The STUDENT-facing list (`/ticket-prices`) is already filtered server-side, so this field exists
   * for the ADMIN editor; a participant never needs to read it.
   */
  roleCodes: z.array(z.string()).nullish().transform((v) => v ?? []),
  selections: z.array(
    z.object({
      groupId: z.guid(),
      eventProductId: z.guid(),
      productRef: z.guid()
    })
  )
});
export const eventTicketListSchema = z.array(eventTicketSchema);

/**
 * INST-PRE — EventTicketPricesDto: GET /events/{id}/ticket-prices.
 *
 * The registration form's payment step needs the ticket price BEFORE registering, and no order
 * exists yet (Commerce creates it asynchronously from the payable event). eventTicketSchema carries
 * no amount, so this is the only source. The server resolves the amount for the APPLICABLE pricing
 * period using the same rule as the catalogue and as the charge itself — the period rule is
 * deliberately not reimplemented here, or the preview and the charge would drift apart.
 *
 * `amount: null` = no price row for that ticket in the applicable period (a free ticket), NOT zero.
 * `installmentsAllowed` is INST-SELL (mig 277) — false => show a single payment only. The real gate
 * is server-side; this is UX.
 */
export const eventTicketResolvedPriceSchema = z.object({
  ticketId: z.guid(),
  ticketName: z.string(),
  sortOrder: z.number().int(),
  amount: z.number().nullish()
});

export const eventTicketPricesSchema = z.object({
  eventId: z.guid(),
  currency: z.string().default('TRY'),
  paymentDueByUtc: isoDate.nullish(),
  installmentsAllowed: z.boolean().optional().default(true),
  items: z.array(eventTicketResolvedPriceSchema),
  /**
   * OFR-REQ: may THIS caller propose a price — i.e. should the buy dialog show "Teklif ver"?
   *
   * ⚠ NOT an authorization result and NOT re-derivable here. The server answers three things at once
   * (purchase permission + partner-staff persona + the tenant having an ACTIVE approval chain); the
   * last one especially cannot be known client-side, and without it the button would only ever lead to
   * a 422. `.default(false)` so a server that predates the field hides the action instead of offering
   * one that fails.
   */
  canOffer: z.boolean().optional().default(false)
});

/**
 * EventPricingListDto — a Commerce pricing list ADDED to an event (ids only; the name is resolved from
 * the Commerce pricing-lists catalog). GET /events/{id}/pricing-lists returns a bare array.
 */
export const eventPricingListSchema = z.object({
  pricingListId: z.guid(),
  sortOrder: z.number().int(),
  // INST-SELL (mig 277): may tickets priced by this list be sold in installments? false => the payer
  // is offered a single payment only. Optional+default true so an older BE (no field) reads as "on",
  // which matches the column default and never hides installments that the server would still offer.
  // NOTE: conditional lists do not price a registration yet, so this switch is stored but dormant —
  // the server reports that as `affectsPaymentToday: false` on every write.
  installmentsEnabled: z.boolean().optional().default(true)
});
export const eventPricingListListSchema = z.array(eventPricingListSchema);

/**
 * EventTicketPriceDto — a per-ticket amount within an event pricing list (amount null = unset). GET
 * /events/{id}/pricing-lists/{pricingListId}/prices returns ALL event tickets (bare array); the PUT
 * SETs the provided rows (amount null clears).
 */
export const eventTicketPriceSchema = z.object({
  ticketId: z.guid(),
  ticketName: z.string(),
  sortOrder: z.number().int(),
  amount: z.number().nullable()
});
export const eventTicketPriceListSchema = z.array(eventTicketPriceSchema);

/**
 * EventPricingPeriodDto — a named date window that scopes ticket prices. startDate/endDate are ISO
 * date strings ("YYYY-MM-DD"); endDate >= startDate; periods within an event may not overlap.
 * GET /events/{id}/pricing-periods returns a bare array (ordered by sortOrder).
 */
export const eventPricingPeriodSchema = z.object({
  id: z.guid(),
  name: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  sortOrder: z.number().int()
});
export const eventPricingPeriodListSchema = z.array(eventPricingPeriodSchema);

export type EventProductGroup = z.infer<typeof eventProductGroupSchema>;
export type EventTicket = z.infer<typeof eventTicketSchema>;
export type EventTicketPrices = z.infer<typeof eventTicketPricesSchema>;
export type EventPricingList = z.infer<typeof eventPricingListSchema>;
export type EventTicketPrice = z.infer<typeof eventTicketPriceSchema>;
export type EventPricingPeriod = z.infer<typeof eventPricingPeriodSchema>;

// ------------------------------ event registration questions (EV questions epic) ---------------
// Admin-authored custom questions rendered in the student registration form. `type` is a FIXED
// UI-widget union (distinct from the exam `questionTypes`); `options` is populated ONLY for
// select/multiselect (empty otherwise). Captured answers are surfaced back on the registration
// roster so an admin can review each registrant's responses. camelCase wire, Zod-parsed at the seam.
export const eventQuestionTypes = ['text', 'textarea', 'number', 'select', 'multiselect', 'toggle', 'date', 'profile'] as const;
export type EventQuestionType = (typeof eventQuestionTypes)[number];

// PROF (mig 244): profile-field keys a 'profile' question can bind to (must match backend ProfileFields).
// mig 331: 'jobPosition' REMOVED — the profile column it read is gone. Must stay in step with the
// backend's ProfileFields.All [STD-DRIFT].
//
// AGECAT-PT (mig 471/473): 'englishAgeCategory' / 'mathAgeCategory' REMOVED — an age band belongs to a
// PROGRAM TYPE, and a tenant can add one at any moment, so the key cannot be a compile-time member of
// this list. It is PARAMETRISED instead (see below). Migration 473 rewrote the stored keys, so no live
// question was left bound to a name nothing resolves.
export const eventProfileFields = [
  'name', 'passportNumber', 'phone', 'birthDate', 'parentGuardianEmail', 'parentGuardianPhone',
  'nationality', 'gender', 'preferredLanguage'
] as const;
export type EventProfileField = (typeof eventProfileFields)[number];

/**
 * AGECAT-PT: prefix of a per-program-type age-category question key — `ageCategory:{programTypeId}`.
 *
 * ⚠ MUST STAY IN STEP WITH `ProfileFields.AgeCategoryPrefix` ON THE BACKEND [STD-DRIFT]. The server
 * validates by SHAPE (prefix + guid), not membership — it cannot resolve the program type across the
 * service boundary [STD-OWN] — so a key built wrongly here is STORED rather than rejected, and then
 * resolves to an empty answer forever: the admin keeps seeing the question, the student keeps
 * answering nothing, and nothing anywhere reports an error. Build keys with the helper below, never
 * by string concatenation at the call site.
 */
export const EVENT_PROFILE_AGE_CATEGORY_PREFIX = 'ageCategory:';

/** The profileField key binding a question to one program type's age band. */
export function eventProfileFieldForProgramType(programTypeId: string): string {
  return `${EVENT_PROFILE_AGE_CATEGORY_PREFIX}${programTypeId}`;
}

/**
 * The program type an age-category question key names, or null when `field` is not one.
 * A malformed key yields null rather than a partial id, so a caller cannot half-resolve it.
 */
export function programTypeIdFromEventProfileField(field: string | null | undefined): string | null {
  if (!field || !field.startsWith(EVENT_PROFILE_AGE_CATEGORY_PREFIX)) return null;
  const id = field.slice(EVENT_PROFILE_AGE_CATEGORY_PREFIX.length);
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id) ? id : null;
}

export const eventQuestionOptionSchema = z.object({ value: z.string(), label: z.string() });
export type EventQuestionOption = z.infer<typeof eventQuestionOptionSchema>;

/** EventQuestionDto — options [] unless type ∈ {select,multiselect}; ordered client-side by orderNo. */
export const eventQuestionSchema = z.object({
  id: z.guid(),
  label: z.string(),
  type: z.enum(eventQuestionTypes),
  isRequired: z.boolean(),
  isActive: z.boolean(),
  orderNo: z.number().int(),
  options: z.array(eventQuestionOptionSchema).nullish().transform((v) => v ?? []),
  // PROF (mig 244): bound profile-field key when type='profile'; null otherwise.
  profileField: z.string().nullish()
});
export const eventQuestionListSchema = pagedListSchema(eventQuestionSchema);
export type EventQuestion = z.infer<typeof eventQuestionSchema>;

/** POST/PUT body for an event question (create/update). options [] unless select/multiselect. */
export interface EventQuestionWriteRequest {
  label: string;
  type: EventQuestionType;
  isRequired: boolean;
  isActive: boolean;
  orderNo: number;
  options: EventQuestionOption[];
  profileField?: string | null;
}

// ------------------------------ EVN: internal event notes (mig 208) -----------------------------
// Staff-only free-text notes on an event. Gated by DEDICATED permissions (engagement.event_notes.
// view / .manage). Not author-locked: anyone with manage can edit/delete any note. authorName /
// updatedByName are DENORMALIZED at write time on the server (JWT 'name'); the client only displays
// them [STD-OWN]. camelCase wire, Zod-parsed at the seam.
export const eventNoteSchema = z.object({
  id: z.guid(),
  body: z.string(),
  createdBy: z.guid().nullish(), // opaque identity uuid (author); display uses authorName
  authorName: z.string().nullish(),
  updatedByName: z.string().nullish(), // last editor's name; null until first edit
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});
export const eventNoteListSchema = pagedListSchema(eventNoteSchema);
export type EventNote = z.infer<typeof eventNoteSchema>;

/** POST/PUT body for an internal event note (engagement.event_notes.manage). */
export interface EventNoteWriteRequest {
  body: string;
}

// ------------------------------ EVC: registered-attendee event content (mig 249) ----------------
// The event's rich (HTML) body shown to REGISTERED attendees — distinct from `description`, which is
// the public blurb on the product page. Read is GATED SERVER-SIDE: only a seat-holding registrant
// (registered|attended) or an engagement.events.update admin gets it; anyone else gets 403. The HTML
// is stored raw and MUST be DOMPurify-sanitized before render (same contract as description).
// `contentHtml` is '' when the admin has not authored anything yet (not a 404).
export const eventContentSchema = z.object({
  contentHtml: z.string(),
  updatedAtUtc: isoDate.nullish()
});
export type EventContent = z.infer<typeof eventContentSchema>;

/** PUT body for the event content (engagement.events.update). Empty string clears the content. */
export interface EventContentWriteRequest {
  contentHtml: string;
}

// ------------------------------ ECF: event content files (mig 250) ------------------------------
// Files attached to the registered-attendee content. METADATA only — the storage key is never on the
// wire [STD-OWN]; bytes come from GET /events/{id}/content/files/{fileId}, gated by the SAME rule as
// the content body. Allowed types are tenant reference data (the `filetypes` definitions group, whose
// value `code` is the extension); the admin UI reads that group to build its accept list, while the
// server independently refuses executables/scripts and enforces 10MB/file and 20 files/event.
export const eventContentFileSchema = z.object({
  id: z.guid(),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  sortOrder: z.number(),
  createdAtUtc: isoDate,
  /**
   * ECF-AGE (mig 476): the age categories this file is open to — EMPTY means unrestricted, which is
   * what every pre-476 file is and stays. Opaque `definitions.age_categories` ids; the admin UI
   * resolves their names from the bands of the EVENT'S program type [STD-OWN].
   *
   * ⚠ THE LIST A STUDENT RECEIVES IS ALREADY FILTERED SERVER-SIDE — a file their band does not open is
   * not in the response at all, and the download endpoint re-checks independently. So this field is
   * for the ADMIN tab's badges, never for deciding what to render to a student: filtering here would
   * be a second, weaker copy of a rule that already ran.
   *
   * `.nullish().transform` mirrors `options` on event questions — a server that omits the field (an
   * older build mid-rollout) yields an unrestricted file, which is the pre-476 behaviour rather than
   * a crash.
   */
  ageCategoryIds: z.array(z.guid()).nullish().transform((v) => v ?? [])
});
export const eventContentFileListSchema = z.array(eventContentFileSchema);
export type EventContentFile = z.infer<typeof eventContentFileSchema>;

/**
 * ECF-AGE — PUT /events/{eventId}/content/files/{fileId}/age-categories body.
 *
 * ⚠ THE FULL SET, ALWAYS. Unlike the profile placements (where absent means "leave alone"), an empty
 * array here CLEARS the restriction and makes the file visible to every registered attendee again —
 * that is the only way back to unrestricted, so it must not be confused with "no change".
 */
export interface EventContentFileAgeCategoriesRequest {
  ageCategoryIds: string[];
}

/** Server-side hard caps for content attachments (mirrored from EventContentFileRules). */
export const eventContentFileLimits = { maxFileBytes: 10 * 1024 * 1024, maxFilesPerEvent: 20 } as const;

// ------------------------------ TRV: attendee travel legs (migs 259/260) ------------------------------
// A registered attendee's ARRIVAL and DEPARTURE travel plan for one event, filled in by the attendee on
// the student event page. One record per direction — the two take an identical parameter set, so the wire
// shape is a pair of identical legs rather than arrival*/departure* field pairs.
//
// The five definition ids are OPAQUE Definitions refs. TWO of them are CHILDREN of
// transportationTypeDefinitionId via the definitions engine's parentId — transporterCompanyDefinitionId
// (the carrier) and locationDefinitionId (the airport/station/port, migs 263/264) — so both pickers narrow
// once a type is chosen. The server does NOT verify either pairing (it cannot read the definitions
// schema), so both cascades are the client's responsibility [STD-OWN].
//
// terminalDefinitionId is NOT nested under the location (a deliberate deferral, mig 263 header), so a
// terminal value carries no indication of which location it belongs to.
//
// `hasTicket` is a FLAG, not a URL: the ticket (PDF or image) is an authorized blob fetched from
// GET .../travel/{direction}/ticket via client.getBlob; the storage key never reaches the client.

export const travelDirections = ['arrival', 'departure'] as const;
export type TravelDirection = (typeof travelDirections)[number];

export const eventTravelLegSchema = z.object({
  direction: z.enum(travelDirections),
  /** DateOnly on the wire (yyyy-MM-dd). */
  travelDate: z.string().nullish(),
  /** TimeOnly on the wire (HH:mm[:ss]). */
  travelTime: z.string().nullish(),
  transportationTypeDefinitionId: z.guid().nullish(),
  transporterCompanyDefinitionId: z.guid().nullish(),
  /** LOC (migs 263/264): airport/station/port — also a CHILD of the transportation type. */
  locationDefinitionId: z.guid().nullish(),
  terminalDefinitionId: z.guid().nullish(),
  countryDefinitionId: z.guid().nullish(),
  flightNumber: z.string().nullish(),
  hasTravelInsurance: z.boolean(),
  hasTicket: z.boolean(),
  updatedAtUtc: isoDate.nullish()
});
export type EventTravelLeg = z.infer<typeof eventTravelLegSchema>;

/** GET /events/{id}/travel — both legs; a leg never filled in comes back all-null (not a 404). */
export const eventTravelSchema = z.object({
  arrival: eventTravelLegSchema,
  departure: eventTravelLegSchema
});
export type EventTravel = z.infer<typeof eventTravelSchema>;

/**
 * PUT /events/{id}/travel/{direction} body. Every field nullable on the wire and the server validates
 * FORMAT only — the "all fields required" rule is enforced by the form (Save stays disabled until the
 * leg is complete), deliberately not by a server completeness check.
 */
export interface EventTravelLegWriteRequest {
  travelDate?: string | null;
  travelTime?: string | null;
  transportationTypeDefinitionId?: string | null;
  transporterCompanyDefinitionId?: string | null;
  locationDefinitionId?: string | null;
  terminalDefinitionId?: string | null;
  countryDefinitionId?: string | null;
  flightNumber?: string | null;
  hasTravelInsurance: boolean;
}

/** Server-side hard caps for the travel ticket (mirrored from EventTravelRules). */
export const eventTravelTicketLimits = {
  maxBytes: 10 * 1024 * 1024,
  accept: 'application/pdf,image/jpeg,image/png,image/webp'
} as const;

// ------------------------------ VIS: attendee visa record (migs 261/262) ------------------------------
// A registered attendee's visa record for one event, filled in by the attendee alongside their travel
// legs. Unlike travel there is NO direction — one record per (event, attendee).
//
// `requiresVisa` is an ANSWER, not a UI flag: false means "asked and answered no". The detail fields stay
// on the record when the box is un-ticked (the server keeps them deliberately, see mig 262) — the form
// only HIDES them, so re-ticking restores what the attendee typed instead of losing it.
//
// visaTypeDefinitionId is an OPAQUE Definitions ref (group `visa_types`); the server validates format
// only and cannot check set membership [STD-OWN].
//
// `hasDocument` is a FLAG, not a URL: the scan (PDF or image) is an authorized blob fetched from
// GET .../visa/document via client.getBlob; the storage key never reaches the client.

export const eventVisaSchema = z.object({
  requiresVisa: z.boolean(),
  visaTypeDefinitionId: z.guid().nullish(),
  /** DateOnly on the wire (yyyy-MM-dd). */
  startsOn: z.string().nullish(),
  endsOn: z.string().nullish(),
  hasDocument: z.boolean(),
  updatedAtUtc: isoDate.nullish()
});
export type EventVisa = z.infer<typeof eventVisaSchema>;

/**
 * PUT /events/{id}/visa body. The details are nullable on the wire and the server enforces exactly ONE
 * rule — endsOn >= startsOn. "Required while the box is ticked" is a form rule (Save stays disabled),
 * deliberately not a server completeness check, so a document can be uploaded before the dates are known.
 */
export interface EventVisaWriteRequest {
  requiresVisa: boolean;
  visaTypeDefinitionId?: string | null;
  startsOn?: string | null;
  endsOn?: string | null;
}

/** Server-side hard caps for the visa document (mirrored from EventVisaRules). */
export const eventVisaDocumentLimits = {
  maxBytes: 10 * 1024 * 1024,
  accept: 'application/pdf,image/jpeg,image/png,image/webp'
} as const;

// ------------------------------ RPT: event travel manifest (mig 265) ------------------------------
// GET /events/{id}/travel-legs — every SEAT-HOLDING attendee's legs for one event, FLAT and paged.
// Cancelled registrations keep their travel row server-side, so the endpoint filters them out; a
// caller does not need to.
//
// Flat on purpose: the manifest GROUPING is the client's job (one flight = many passengers), and a
// grouped wire shape would mean a second projection for export, which wants the flat rows anyway.
//
// `hasTicket` is a FLAG; bytes come from GET .../travel/{direction}/ticket?userRef= [STD-OWN].

export const eventTravelReportRowSchema = z.object({
  userRef: z.guid(),
  /** Denormalized at register time (mig 241); null on older rows. */
  registrantName: z.string().nullish(),
  registrationStatus: z.string(),
  direction: z.enum(travelDirections),
  /** DateOnly on the wire (yyyy-MM-dd). */
  travelDate: z.string().nullish(),
  /** TimeOnly on the wire (HH:mm[:ss]). */
  travelTime: z.string().nullish(),
  transportationTypeDefinitionId: z.guid().nullish(),
  transporterCompanyDefinitionId: z.guid().nullish(),
  locationDefinitionId: z.guid().nullish(),
  terminalDefinitionId: z.guid().nullish(),
  countryDefinitionId: z.guid().nullish(),
  flightNumber: z.string().nullish(),
  hasTravelInsurance: z.boolean(),
  hasTicket: z.boolean(),
  updatedAtUtc: isoDate.nullish()
});
export const eventTravelReportListSchema = pagedListSchema(eventTravelReportRowSchema);
export type EventTravelReportRow = z.infer<typeof eventTravelReportRowSchema>;

/** The Definitions group the visa type picker reads (mig 261) — flat, no parent cascade. */
export const visaTypeGroupKey = 'visa_types';

// EV reminders epic — per-event reminder RULES authored by an admin (the backend fires them). Each
// rule targets a tenant EMAIL template and/or a tenant NOTIFICATION (in-app/push) template BY KEY,
// on a schedule: fire every intervalValue intervalUnit, stopping latestValue latestUnit before the
// event. At least one template key is required (enforced client-side + on the server). camelCase
// wire, Zod-parsed at the seam.
export const intervalUnits = ['hour', 'day'] as const;
export type IntervalUnit = (typeof intervalUnits)[number];

/** EventReminderDto — a single reminder rule for an event. Either template key may be null, but not both. */
export const eventReminderSchema = z.object({
  id: z.guid(),
  emailTemplateKey: z.string().nullish().transform((v) => v ?? null),
  notificationTemplateKey: z.string().nullish().transform((v) => v ?? null),
  intervalValue: z.number().int(),
  intervalUnit: z.enum(intervalUnits),
  latestValue: z.number().int(),
  latestUnit: z.enum(intervalUnits),
  isActive: z.boolean()
});
export const eventReminderListSchema = pagedListSchema(eventReminderSchema);
export type EventReminder = z.infer<typeof eventReminderSchema>;

/** POST/PUT body for an event reminder rule. At least one of the two template keys must be non-null. */
export interface EventReminderWriteRequest {
  emailTemplateKey: string | null;
  notificationTemplateKey: string | null;
  intervalValue: number;
  intervalUnit: IntervalUnit;
  latestValue: number;
  latestUnit: IntervalUnit;
  isActive: boolean;
}

/** A captured answer to an event question. value shape follows the question `type`. */
export type EventQuestionAnswerValue = string | string[] | boolean | number;
export const eventQuestionAnswerSchema = z.object({
  questionId: z.string(),
  value: z.union([z.string(), z.array(z.string()), z.boolean(), z.number()])
});
export type EventQuestionAnswer = z.infer<typeof eventQuestionAnswerSchema>;

/** Where a registration came from — mirrors RegistrationSources (DB ck_event_registrations_source). */
export const eventRegistrationSources = ['self', 'teacher_invited'] as const;

/**
 * RegistrationDto — one event registration (roster row); userRef is opaque (P5-EN). #3: +waitlisted.
 *
 * `awaiting_payment` (mig 406) is a SEAT-HOLDING status, exactly like `registered`: the registrant took
 * a seat out of inventory and owes money for it. A registration reads `registered` only once
 * payment_state settles to `not_required` or `paid` — the same rule that unlocks event content, so the
 * status and the locked content can no longer contradict each other.
 *
 * ⚠ STRICT ENUM, so this list must gain a new server value BEFORE the server can emit it: a value
 * missing here throws inside `.parse` and blanks whatever page read it (the roster, the register
 * response). That is why the frontend ships ahead of the migration.
 */
export const eventRegistrationStatuses = ['registered', 'awaiting_payment', 'cancelled', 'attended', 'waitlisted'] as const;

/**
 * Statuses that HOLD A SEAT — the client-side mirror of the server's `RegistrationStatuses.SeatHolding`.
 *
 * ⚠ EXPORTED SO NOBODY SPELLS IT OUT AGAIN. Five places in three SPAs had written
 * `status === 'registered' || status === 'attended'` by hand, and adding `awaiting_payment` to the
 * server broke every one of them silently — most visibly the student event page, which decided the
 * holder had no seat and offered the WAITLIST message instead of the payment panel, so a student who
 * owed money had no way left to pay it. The backend hit the identical problem in nine query WHERE
 * clauses; both sides now read one list.
 *
 * ⚠ HOLDING A SEAT IS NOT THE SAME AS BEING PAID UP, and it never means "may read the content": the
 * content gate is seat-holding AND payment-settled, and `awaiting_payment` deliberately fails the
 * second half.
 */
export const seatHoldingRegistrationStatuses = ['registered', 'awaiting_payment', 'attended'] as const;

/** Does this registration status hold a seat? Nullish (not registered) is false. */
export function holdsSeat(status: string | null | undefined): boolean {
  return status != null && (seatHoldingRegistrationStatuses as readonly string[]).includes(status);
}

/**
 * REG-CANCEL — payment states in which a participant may still cancel their OWN registration; the
 * client-side mirror of the server's `RegistrationCancelPolicy.CancellableStates`.
 *
 * ⚠ AN ALLOW-LIST, NOT A DENY-LIST, and for the same reason the server's is: a payment state added
 * later must close the button (annoying, and visible) rather than open it (a student releasing a seat
 * they paid for). Spelling out `!== 'paid'` anywhere is the bug this export exists to prevent — the
 * blocking set is four values, not one.
 *
 * ⚠ THIS IS UX, NOT ENFORCEMENT. The server decides, and it decides on TWO inputs: this state AND the
 * collected amount (`event_registrations.amount_paid`, mig 409), which the client is not told. The
 * states below are also a PROJECTION of Commerce and lag it by an outbox hop, so a student who paid
 * seconds ago can still see an enabled button. Both gaps end the same way — a 409 whose body must be
 * rendered — so never treat a hidden button as the guarantee.
 */
export const cancellablePaymentStates = ['not_required', 'awaiting_plan', 'plan_active'] as const;

/**
 * May this registration still be cancelled by its owner, as far as the client can tell?
 * Nullish is treated as `not_required` — a free/ticketless registration, which is cancellable.
 */
export function canCancelRegistration(paymentState: string | null | undefined): boolean {
  return (cancellablePaymentStates as readonly string[]).includes(paymentState ?? 'not_required');
}
export const eventRegistrationSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  userRef: z.guid(),
  status: z.enum(eventRegistrationStatuses),
  registeredAtUtc: isoDate,
  cancelledAtUtc: isoDate.nullish(),
  // Ticket epic — the chosen ticket (null for plain/legacy registrations).
  ticketId: z.guid().nullish(),
  waitlistPosition: z.number().int().nullish(), // #3: dolu = kuyrukta (1-tabanlı sıra)
  // A-22b: kayıt-düzeyi koruma — roster'da yalnız engagement.safeguarding.read ile dolu; aksi halde null.
  // REG-NAME (mig 241): registrant's display name, denormalized at register time (null on older rows).
  registrantName: z.string().nullish(),
  // EVT-PAY (mig 272): commerce taksit planı durumunun projeksiyonu. Etkinlik içeriği yalnız
  // not_required|paid iken açılır; roster'da "kim hâlâ ödemedi" görünümünü besler. Eski satırlarda
  // absent olabileceği için varsayılanı not_required (geri uyumlu, kapıyı GEVŞETMEZ — sunucu karar verir).
  paymentState: z
    .enum(['not_required', 'awaiting_plan', 'plan_active', 'awaiting_approval', 'partially_paid', 'paid', 'defaulted'])
    .nullish()
    .transform((v) => v ?? 'not_required'),
  // REG-2AX (mig 439) — the coarse pair; see registrationPaymentStatuses for why both are nullish.
  //
  // ⚠ NULL ON THE REGISTER RESPONSE TOO, and that is not a bug: the row is created in the same
  // SaveChanges, and EF does not read a stored generated column back after the INSERT. Draw the badge
  // from the next read (event detail / roster), never from the POST result.
  paymentStatus: coarsePaymentStatusField,
  // ⚠ `registrationStatus`, NOT `coarseRegistrationStatus` — THE ROSTER DTO SPELLS IT DIFFERENTLY FROM
  // EVERY OTHER ONE, and this schema had the other spelling for as long as mig 439 has existed.
  //
  // Zod STRIPS a key it does not declare, so the real field never arrived and the value read
  // `undefined` forever. The admin roster then fell through to its lifecycle fallback and printed
  // "Ödeme Bekleniyor" for a student whose own page said "Kayıtlısın" — one registration, two screens,
  // two answers, which is the exact bug mig 439 was created to end.
  //
  // ⚠ THE SYMPTOM POINTED AWAY FROM THE CAUSE. `paymentStatus` matches on both sides, so the Payment
  // column worked perfectly while the Status column beside it did not; that made it read as a display
  // decision rather than a field that never arrived. If one of a pair works and the other silently
  // does not, suspect the KEY before the logic.
  //
  // Why the server spells it three ways, all deliberate:
  //   RegistrationDto        -> registrationStatus        (this one; `status` is the lifecycle)
  //   EventDetailDto         -> myCoarseRegistrationStatus (`myRegistrationStatus` = the lifecycle)
  //   Teacher registration   -> coarseRegistrationStatus
  // Do not "harmonise" this field name without changing the wire it is copied from.
  registrationStatus: coarseRegistrationStatusField,
  chaperoneRef: z.guid().nullish(),
  emergencyContactName: z.string().nullish(),
  emergencyContactPhone: z.string().nullish(),
  // EV questions epic — the registrant's answers to the event's custom questions (empty when none).
  // Forward/back-compat: absent on legacy rows → []. The admin roster resolves labels via the
  // event's question list (questionId → label).
  answers: z.array(eventQuestionAnswerSchema).nullish().transform((v) => v ?? []),
  // CF (mig 253): this user's LATEST consent for the event; null = none on file (the event has no
  // consent form, or they registered before one was uploaded — consent is only captured at register time).
  consentedAtUtc: isoDate.nullish(),
  // VIS (mig 262): the attendee's visa record, roster-only. THREE-STATE on purpose:
  //   null  = never asked (no row) — the state an organiser still has to chase,
  //   false = "I do not need a visa" (asked and answered),
  //   true  = needs one; the detail fields below are then meaningful.
  // Collapsing null and false into one boolean would hide exactly the difference the roster exists for.
  visaRequired: z.boolean().nullish(),
  visaTypeDefinitionId: z.guid().nullish(),
  /** DateOnly on the wire (yyyy-MM-dd). */
  visaStartsOn: z.string().nullish(),
  visaEndsOn: z.string().nullish(),
  /** A FLAG, not a URL — staff fetch the bytes from GET .../visa/document?userRef=. */
  visaHasDocument: z.boolean().nullish().transform((v) => v ?? false),
  // TCH-TKT (mig 282): how this person got in — 'self' (registered and paid for themselves) or
  // 'teacher_invited' (used a ticket their teacher bought and assigned). NOT the ticket product type
  // (that is ticketId). Written at register time and never recomputed; legacy rows → 'self'.
  source: z.enum(eventRegistrationSources).nullish().transform((v) => v ?? 'self')
});
export const eventRegistrationListSchema = pagedListSchema(eventRegistrationSchema);

// ------------------------------ TEACHER EVENT PORTAL (Engagement :5800) --------------------------
// TCH-TKT (migs 281/282/284) — the teacher's own surfaces. All of these are scoped SERVER-SIDE to
// "my students" / "my quota"; the FE never sends a student-id list (that would be an IDOR).
// Endpoints under /api/v1/engagement/events/{eventId}/teacher/...

/**
 * One answer as the TEACHER sees it. `masked: true` means the value is a profile-sourced field
 * (passport number and the like) and the server did NOT send it — `value` is then null. Masking
 * happens server-side on purpose: masking in the browser would already have shipped the value.
 */
export const teacherAnswerSchema = z.object({
  questionId: z.string(),
  label: z.string().nullish(),
  value: z.union([z.string(), z.array(z.string()), z.boolean(), z.number()]).nullish(),
  masked: z.boolean().optional().default(false)
});

/**
 * TeacherRegistrationRowDto — Tab 2 row: one of MY students on this event, with completion flags the
 * SERVER computes (the FE must not re-derive them, or "travel complete" would drift).
 */
export const teacherRegistrationRowSchema = z.object({
  registrationId: z.guid(),
  userRef: z.guid(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  displayName: z.string(),
  status: z.enum(eventRegistrationStatuses),
  source: z.enum(eventRegistrationSources),
  paymentState: z
    .enum(['not_required', 'awaiting_plan', 'plan_active', 'awaiting_approval', 'partially_paid', 'paid', 'defaulted'])
    .nullish()
    .transform((v) => v ?? 'not_required'),
  // REG-2AX (mig 439) — see registrationPaymentStatuses.
  paymentStatus: coarsePaymentStatusField,
  coarseRegistrationStatus: coarseRegistrationStatusField,
  registeredAtUtc: isoDate,
  /** Arrival AND departure both entered (the product rule; a single leg is NOT complete). */
  travelComplete: z.boolean(),
  /** 0/1/2 — lets the UI say "no return leg" instead of a bare cross. */
  travelLegCount: z.number().int(),
  /** PRESENCE only: identity.user_passports has a row. The number stays behind sensitive_view. */
  passportComplete: z.boolean(),
  questionsComplete: z.boolean(),
  missingRequiredAnswerCount: z.number().int(),
  /** null = the event has no consent form, so the column is not applicable (render '—', not a cross). */
  consentComplete: z.boolean().nullish(),
  answers: z.array(teacherAnswerSchema).nullish().transform((v) => v ?? [])
});
export const teacherRegistrationListSchema = pagedListSchema(teacherRegistrationRowSchema);

/** TeacherTravelRowDto — Tab 4 row. hasTicket is a FLAG; bytes come from the per-user endpoint. */
export const teacherTravelRowSchema = z.object({
  userRef: z.guid(),
  displayName: z.string(),
  direction: z.enum(['arrival', 'departure']),
  /** DateOnly / TimeOnly on the wire. */
  travelDate: z.string().nullish(),
  travelTime: z.string().nullish(),
  transportationTypeDefinitionId: z.guid().nullish(),
  transporterCompanyDefinitionId: z.guid().nullish(),
  locationDefinitionId: z.guid().nullish(),
  terminalDefinitionId: z.guid().nullish(),
  countryDefinitionId: z.guid().nullish(),
  flightNumber: z.string().nullish(),
  hasTravelInsurance: z.boolean().optional().default(false),
  hasTicket: z.boolean().optional().default(false)
});

/** TeacherVisaRowDto — Tab 5 row. requiresVisa is THREE-STATE: null = never asked. */
export const teacherVisaRowSchema = z.object({
  userRef: z.guid(),
  displayName: z.string(),
  requiresVisa: z.boolean().nullish(),
  visaTypeDefinitionId: z.guid().nullish(),
  startsOn: z.string().nullish(),
  endsOn: z.string().nullish(),
  hasDocument: z.boolean().optional().default(false)
});

/** Ticket instance lifecycle — mirrors TicketAllocationStatuses (DB CHECK). */
export const ticketAllocationStatuses = [
  'available',
  'assigned',
  'waiting_for_information',
  'completed',
  'released'
] as const;

/**
 * Batch payment projection — mirrors TicketBatchPaymentStates. No 'not_required': batches are priced.
 *
 * ⚠ OFR (mig 342) ADDED TWO MEMBERS, AND THIS UNION IS LOAD-BEARING. A batch state the enum does not
 * know fails the parse of `ticketBatchSchema`, which fails `teacherTicketsSchema`, which blanks the
 * WHOLE "Biletlerim" tab — not one row, not one field. So this list must be widened in the same change
 * that lets the server emit the new value, never after.
 *
 * `awaiting_offer` / `offer_rejected` both sit BEFORE the payment path: no Commerce order exists for
 * them (none was ever opened), which is why `canAssign` is false and no payment control may show.
 */
export const ticketBatchPaymentStates = [
  'awaiting_offer',
  'offer_rejected',
  'awaiting_plan',
  // PAY-DOC (mig 360): bir taksidin dekontu inceleniyor. 'plan_active'in ALT durumu — para gelmedi,
  // borc duruyor, ATAMA ACIK kalir. Bu deger burada YOKSA sunucu onu gonderdigi anda z.enum atar ve
  // parse alim satirini degil, onu iceren TUM listeyi dusurur: ekran bir hucre degil, bosalir.
  'awaiting_approval',
  'plan_active',
  // PAY-DOC (mig 361): en az bir taksit odendi, alim bitmedi (0 < odenen < toplam). 'plan_active'
  // yalniz "plan var mi" derdi; 3'te 1'i odenmis bir alim ile hic odenmemis olan ayni gorunuyordu.
  'partially_paid',
  'paid',
  'defaulted',
  // BATCH-CANCEL (mig 464): the BUYER cancelled an unpaid purchase. Seats released, order closed,
  // nothing owed. TERMINAL, and deliberately NOT 'defaulted' — that value means a payment FAILED, it
  // is the one chip rendered in the inverted collections style, and it is what a dunning report
  // selects on. Telling a partner who owed nothing and did the tidy thing that they defaulted would
  // put them on a list of people to chase.
  'cancelled'
] as const;

/** OFR: the offer's own lifecycle — mirrors OfferStatuses. Null on a plain (non-negotiated) purchase. */
export const offerStatuses = ['pending', 'approved', 'rejected', 'withdrawn', 'expired'] as const;

/**
 * BATCH-QTY (mig 354) — the ADMIN view of one ticket batch, from
 * `GET /api/v1/engagement/ticket-batches/{id}`. Distinct from `ticketBatchSchema`, which answers
 * "what is in MY quota" for the buyer; this one answers "what may I correct, and how far down".
 *
 * ⚠ `minQuantity` AND `canEditQuantity` ARE SERVER-COMPUTED. Do NOT re-derive either in the client.
 * The floor is the higher of two independent bounds — seats already assigned or funded (never
 * revoked, FUND D3) and the amount already collected — and reproducing that here would need the
 * funding percentage and the paid/assigned split. Any drift shows up as a form that happily submits
 * a request the server answers with 409.
 */
export const ticketBatchAdminSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  ticketId: z.guid(),
  ticketName: z.string(),
  buyerUserRef: z.guid(),
  quantity: z.number().int(),
  unitAmount: z.number(),
  totalAmount: z.number(),
  currency: z.string(),
  /** Kept a plain string, NOT z.enum: a state the client does not know must not blank the card. */
  paymentState: z.string(),
  amountPaid: z.number(),
  paymentDueBy: isoDate.nullish(),
  assignedSeats: z.number().int(),
  fundedSeats: z.number().int(),
  minQuantity: z.number().int(),
  canEditQuantity: z.boolean(),
  /**
   * OFR-CAP (mig 370): this ORDER's seat-opening threshold and WHERE it comes from.
   *
   * ⚠ `source` is not decoration. Three levels resolve into one number (order → event → platform), and
   * a card that shows only the number produces the support question "I set the event to 30, why does
   * this order say 20?". `.nullish()` so a server that predates the field renders the card without the
   * threshold block rather than blanking the whole page.
   */
  threshold: z
    .object({
      batchId: z.guid(),
      effectivePercent: z.number().int(),
      source: z.enum(['order', 'event', 'platform']),
      orderOverride: z.number().int().nullish(),
      eventPercent: z.number().int().nullish(),
      platformDefault: z.number().int(),
      /** The percentage turned into MONEY — the figure the partner's row shows. */
      seatOpenThresholdAmount: z.number(),
      currency: z.string()
    })
    .nullish(),
  /**
   * OFR-CAP (mig 369): may the CALLER change this order's threshold — i.e. do they hold a role that the
   * batch's offer chain granted `can_update_threshold`?
   *
   * ⚠ Surface, not authorization (same standing as `canEditQuantity`): the real gate is the command and
   * it 403s. False on an order with no offer at all, which is v1's deliberate limit — the card renders
   * read-only and says so.
   */
  canUpdateThreshold: z.boolean().optional().default(false)
});

/** BATCH-QTY: the client sends the count it wants the batch to BE, never a delta. */
export interface UpdateTicketBatchQuantityRequest {
  quantity: number;
}

/**
 * OFR-CAP (mig 370): this order's seat-opening threshold. `percent: null` CLEARS the override — it means
 * "this order has no opinion, follow the event (then the platform)", which is NOT the same as sending 20.
 */
export interface UpdateBatchFundingThresholdRequest {
  percent: number | null;
}

/**
 * FUND-AMT: how much of a purchase's arrived money goes to each student.
 *
 * ⚠ ONE REQUEST FOR THE WHOLE SPLIT. Distributing 10.000 across six students is one decision; sent seat
 * by seat it hits the pot ceiling half-way and leaves a partly-applied split with no undo. The server
 * validates the set and writes all of it or none. A single inline edit sends one item through the same
 * endpoint.
 *
 * Seats not listed are left ALONE — omission is not "set to zero".
 */
export interface SetTicketPaidAmountsRequest {
  items: { allocationId: string; amount: number }[];
}

export const ticketAllocationSchema = z.object({
  id: z.guid(),
  batchId: z.guid(),
  ticketId: z.guid(),
  status: z.enum(ticketAllocationStatuses),
  assignedUserRef: z.guid().nullish(),
  /** Resolved by the server from Identity; null once the student is no longer this teacher's. */
  assignedStudentName: z.string().nullish(),
  assignedAtUtc: isoDate.nullish(),
  claimedAtUtc: isoDate.nullish(),
  registrationId: z.guid().nullish(),
  /**
   * FUND (mig 351): when this seat was marked PAID. Non-null = funded, and funded is the ONLY state in
   * which the seat reaches the student — an assigned-but-unfunded seat is invisible to them and the
   * server refuses the claim.
   *
   * `.nullish()` rather than required: a server that predates the field reads as "not funded", which
   * hides the seat rather than promising one that cannot be claimed.
   */
  paidSelectedAtUtc: isoDate.nullish(),
  /**
   * FUND-AMT (mig 367) — how much of the purchase's arrived money is allocated TO THIS STUDENT.
   *
   * Below the seat's threshold (`batch.seatOpenThresholdAmount`) it is still stored: a partner who
   * collected 300 of a 400 share has a real number to record, and the seat simply stays closed.
   */
  paidAmount: z.number().optional().default(0),
  /**
   * FUND-AMT — what is still to be collected for this seat (ticket price − allocated, floored at 0).
   *
   * ⚠ SERVER-COMPUTED, and deliberately not `batch.unitAmount - paidAmount` here: the seat renders in a
   * child table and a subtraction in this file is one refactor away from using the wrong order's unit
   * price.
   */
  remainingAmount: z.number().optional().default(0),
  /**
   * FUND-AMT — can the student see the event's registered-only content yet? The same fact as
   * `paidSelectedAtUtc != null`, named for what it means.
   *
   * ⚠ DO NOT re-derive it from `paidAmount >= seatOpenThresholdAmount`. Once open, a seat stays open
   * (2026-08-17): lowering the amount re-apportions money and leaves the content alone, so an open seat
   * can legitimately sit below its threshold — and the threshold comparison would call it closed.
   */
  contentOpen: z.boolean().optional().default(false)
});

export const ticketBatchSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  ticketId: z.guid(),
  ticketName: z.string(),
  quantity: z.number().int(),
  unitAmount: z.number(),
  totalAmount: z.number(),
  currency: z.string(),
  paymentState: z.enum(ticketBatchPaymentStates),
  /** OPAQUE commerce plan id — the installment panel opens with this; null before a plan exists. */
  paymentPlanRef: z.guid().nullish(),
  paymentDueByUtc: isoDate.nullish(),
  /**
   * Server-derived: may seats be handed out at all? Never re-derive in the FE.
   *
   * ⚠ FUND CHANGED WHAT THIS MEANS. It used to be "the batch is fully paid"; it is now "a live
   * purchase exists" — true while payment is still running, false only when the offer is undecided or
   * rejected, or the plan defaulted. The money question moved to `maxFundedSeats`.
   */
  canAssign: z.boolean(),
  /**
   * TKT-ASG (2026-08-18): WHY `canAssign` is what it is — a machine token, never a sentence.
   *
   * `payment_state` (no live purchase: offer undecided/rejected, or the plan defaulted) ·
   * `funding` (a direct purchase, and the money has not opened another seat yet) ·
   * `offer_approved` (an approved price request — assignment is open with no money at all) ·
   * `free` (a free ticket) · `funded` (the allowance has room).
   *
   * ⚠ NULLISH on purpose: a server that predates this field must not blank the whole tickets tab. The UI
   * falls back to its previous, less specific copy when it is missing.
   */
  assignReason: z.string().nullish(),
  /**
   * TKT-ASG: how much more money opens ONE more seat — meaningful only when `assignReason` is `funding`,
   * otherwise 0.
   *
   * ⚠ SERVER-COMPUTED. It is `(seatsHeld + 1) × threshold − amountPaid`, and every term of that is a
   * server concept (the threshold resolves three levels deep). Re-deriving it here is the mistake
   * `seatOpenThresholdAmount` already exists to prevent.
   */
  payToOpenNextSeat: z.number().optional().default(0),
  createdAtUtc: isoDate,
  allocations: z.array(ticketAllocationSchema).nullish().transform((v) => v ?? []),
  /**
   * TCH-SELF (B4): is this batch's ticket open to the CALLER's own role — i.e. may they keep one of its
   * instances for themselves (`assign-to-me`)?
   *
   * Batch-level rather than per-allocation because a batch carries exactly ONE ticket, so the role rule is
   * the same for every instance in it.
   *
   * ⚠ NOT an authorization result — the real gate is server-side and 403s. This only stops the UI offering
   * a button that cannot work. `.default(false)` so a server that predates the field hides the action
   * rather than showing one that fails.
   */
  canAssignToSelf: z.boolean().optional().default(false),
  /**
   * OFR-REQ: this purchase started as a PRICE OFFER, and this is that offer's status. Null on a plain
   * purchase — the row must not read as a negotiation when it was not one.
   *
   * ⚠ It explains `paymentState`, it does not replace it. `awaiting_offer` says "payment is not open";
   * this says WHY, and distinguishes rejected from withdrawn from expired — three outcomes the batch
   * state deliberately collapses into one (`offer_rejected`), because none of them opens a payment.
   */
  offerStatus: z.enum(offerStatuses).nullish(),
  /**
   * OFR-APR — WHY this purchase is waiting for its first payment: `offer_approved` (an approved price
   * request opened the payment step) or `direct` (bought at list price). Null when it is not waiting.
   *
   * ⚠ SERVER-DERIVED, AND NOT THE SAME AS `offerStatus === 'approved'`. The answer is the PAIR of the
   * offer status and `paymentState`: an approved offer that is fully paid is waiting for nothing, and
   * `awaiting_plan` on its own does not say which route got here. Deriving it in the client would put
   * a third copy of that rule next to the admin report's and the partner tab's.
   *
   * ⚠ `.nullish()` NOT `.default(...)`: absence is meaningful (not waiting), so there is no safe
   * default to invent. A server that predates the field simply says nothing and the chip falls back
   * to the plain "awaiting payment" wording — the behaviour before this feature existed.
   */
  awaitingPaymentReason: z.enum(['direct', 'offer_approved']).nullish(),
  // ⚠ NO offerCurrentRoleCode HERE, DELIBERATELY. The approval chain's steps are the tenant's
  // INTERNAL sign-off structure; a partner is an outside party and needs to know that a decision is
  // pending, not whose signature is next. The server does not send it on this surface at all — a
  // field hidden only in the UI still travels in the JSON and is readable in devtools. The role
  // stays on the ADMIN queue (eventTicketOfferQueueItemSchema.currentRoleCode).
  /** OFR-APR: the rejecting step's note — the ONLY explanation the partner gets. Required server-side. */
  offerRejectionNote: z.string().nullish(),
  /** OFR-REQ: the list price at offer time. `unitAmount` is the OFFERED price; without this the partner
   *  cannot see how big a discount they asked for. */
  offerListUnitAmount: z.number().nullish(),
  /**
   * FUND (mig 351) — the funding counters. Every one of these is SERVER-DERIVED and none of them may be
   * recomputed here: the same figures gate the write, and a second copy of the rule in the browser would
   * disagree with it the first time either changes.
   *
   * `.default()` on all of them so a server that predates FUND reads as "nothing funded, nothing
   * fundable" — the safe direction: it hides the control rather than offering one the server refuses.
   */
  /** Cumulative money received against this purchase, projected from Commerce. */
  amountPaid: z.number().optional().default(0),
  /** Seats currently marked paid. */
  fundedSeats: z.number().int().optional().default(0),
  /**
   * The most seats the money received can cover.
   *
   * ⚠ CAN BE LESS THAN `fundedSeats`. A refund or a default lowers `amountPaid` but never revokes a
   * funded seat (the student may already have travelled), so the purchase reads as OVER-COMMITTED —
   * see `fundingOverCommitted`.
   */
  maxFundedSeats: z.number().int().optional().default(0),
  /** The share of the unit price each funded seat consumes (per event; platform default 20). */
  fundingMinPercent: z.number().int().optional().default(20),
  /**
   * FUND (D2): the payment deadline has passed but the seats were NOT released, because something has
   * been paid. A flag, not a state — `paymentState` is unchanged and the payment path stays open.
   */
  fundingOverdue: z.boolean().optional().default(false),
  /**
   * FUND (D3): more money is allocated to seats than has arrived (a refund or a default).
   *
   * ⚠ FUND-AMT reads this from MONEY (`allocatedAmount > amountPaid`), not from a seat count. No seat is
   * ever closed by it — the student may already have travelled.
   */
  fundingOverCommitted: z.boolean().optional().default(false),
  /**
   * FUND-AMT (mig 367) — the money view of this purchase, which is what the seat columns and the split
   * panel are built on. Server-derived, same discipline as the FUND counters above.
   */
  /** Total allocated across this purchase's seats (`SUM(paidAmount)`). */
  allocatedAmount: z.number().optional().default(0),
  /** Arrived but not yet written to any student — the pot the split panel may distribute. Never negative. */
  unallocatedAmount: z.number().optional().default(0),
  /**
   * What one seat must hold to OPEN (the percentage turned into money).
   *
   * ⚠ Never recompute this from `fundingMinPercent`: the percentage can be overridden per event and the
   * rounding rule (up to the cent) is the server's. A second copy in the browser would disagree with the
   * 409 the command returns.
   */
  seatOpenThresholdAmount: z.number().optional().default(0)
});

export const teacherTicketsSchema = z.object({
  batches: z.array(ticketBatchSchema).nullish().transform((v) => v ?? []),
  totalTickets: z.number().int(),
  assignedTickets: z.number().int(),
  availableTickets: z.number().int()
});

/** A student who may still receive a ticket (already-registered / already-holding are excluded server-side). */
export const assignableStudentSchema = z.object({
  userId: z.guid(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  displayName: z.string(),
  /**
   * TCH-BULK: OPAQUE definitions.definitions.id (group 'country') — the filter dimension for bulk seat
   * assignment, where a partner's roster runs to hundreds of names across countries.
   *
   * ⚠ NULL means UNKNOWN, not "no country": the student has not filled their profile. Render it as such —
   * bucketing them into a country because the field is empty is how a filter starts lying.
   */
  countryDefinitionId: z.guid().nullish(),
  /**
   * TCH-BULK: the sign-in identifiers, so a picker can tell two people with the same name apart.
   *
   * ⚠ NOT DECORATION. Display names repeat in real data (the demo tenant carries three "Ada Lovelace"),
   * so a seat picker showing names alone cannot answer "which of these is the student in front of me" —
   * and ticking the wrong row gives a paid seat to the wrong person.
   */
  email: z.string().nullish(),
  username: z.string().nullish()
});

/**
 * TCH-INV F3 (mig 292) — a teacher's invitation to one student for one event.
 *
 * TWO KINDS, and they differ in exactly one thing: `ticket` hands over an ALREADY PAID allocation
 * (allocationRef holds the seat), `open` reserves NOTHING and the student joins the normal paid flow —
 * so an open invitation promises nothing about availability and its copy must not imply a seat.
 */
export const eventInvitationKinds = ['ticket', 'open'] as const;
export const eventInvitationStatuses = ['pending', 'accepted', 'withdrawn'] as const;

export const eventInvitationSchema = z.object({
  id: z.guid(),
  studentUserRef: z.guid(),
  studentName: z.string(),
  kind: z.enum(eventInvitationKinds),
  status: z.enum(eventInvitationStatuses),
  /** Set for kind='ticket' only — the allocation that holds the seat. */
  allocationRef: z.guid().nullish(),
  message: z.string().nullish(),
  notifiedAtUtc: z.string().nullish(),
  respondedAtUtc: z.string().nullish(),
  createdAtUtc: z.string()
});
export const eventInvitationListSchema = z.array(eventInvitationSchema);

/**
 * One row of a BULK invite. `invited: false` is NOT an error — it is an expected outcome in a bulk
 * operation (already registered, already invited, no ticket left), and the UI must show it per student
 * instead of collapsing the request into one success/failure.
 */
export const eventInvitationOutcomeSchema = z.object({
  studentUserRef: z.guid(),
  studentName: z.string(),
  invited: z.boolean(),
  /** already_registered | already_invited | already_holds_ticket | no_ticket_available */
  reason: z.string().nullish(),
  invitationId: z.guid().nullish()
});

export const inviteStudentsResultSchema = z.object({
  outcomes: z.array(eventInvitationOutcomeSchema).nullish().transform((v) => v ?? []),
  invitedCount: z.number().int(),
  skippedCount: z.number().int()
});

/**
 * POST /events/{id}/teacher/invitations — an invite RESERVES NO SEAT.
 *
 * ⚠ INV-NOSEAT: `kind` and `ticketId` were REMOVED from this body, server side too. Inviting a student and
 * giving them a ticket are separate actions: this creates the invitation, and a ticket is assigned through
 * `POST …/teacher/allocations`. `eventInvitationKinds` still exists because READS need it — existing rows say
 * 'ticket' and the roster renders them — but nothing can be WRITTEN as a ticket invitation any more.
 */
export interface InviteStudentsRequest {
  studentUserRefs: string[];
  message?: string | null;
}

/**
 * TCH-INV F2 — POST /api/v1/users/my-students (IDENTITY, not Engagement).
 *
 * ⚠ THERE IS STILL NO roleCodes FIELD. `roleId` below is not the same thing: it may only name a role of an
 * ASSIGNABLE type — participant (student, parent …) or supervisor (teacher) — which the server re-checks. A
 * teacher still cannot mint an administrator. The old free-form role list stays admin-only.
 */
export interface InviteMyStudentRequest {
  /**
   * STU-HANDLE (mig 314): OPTIONAL. Omit it when the teacher has no address for this student — the server
   * then mints a username and a one-time password instead of e-mailing an activation link.
   */
  email?: string;
  // USR-PARTS (mig 311): the name of record. The server still accepts a displayName and splits it, but a
  // teacher typing into two labelled boxes has already answered the question the split would guess at.
  // ⚠ Both are REQUIRED when there is no e-mail: the username is built from the initials, and a student with
  // neither a name nor an address would render as nothing at all.
  firstName: string;
  lastName: string;
  /** USR-PARTS: optional, and part of the same generated full_name as the other two. */
  middleName?: string;
  /**
   * TCH-USR: both optional and both validated server-side against the SAME lists the pickers are fed from
   * (GET /users/my-students/registration-options). Omit `roleId` and the account becomes a student, which is
   * what every invite did before these fields existed.
   */
  schoolId?: string;
  roleId?: string;
}

/**
 * TCH-USR: one pickable school or role. `isDefault` marks the role an omitted `roleId` means.
 *
 * TCH-XLSX-SCOPE: `countryDefinitionId` is the school's own country — an opaque definitions id, present so a
 * Country picker can cascade into the School one without a second request. Always null on a role.
 *
 * ⚠ NULL IS NOT "ANY COUNTRY". A school with no country recorded is in NO country's filtered list, matching
 * the server, so a client that filters MUST count them and say so — on the demo tenant that is every school,
 * and an unexplained empty dropdown reads as a broken page.
 */
export const studentRegistrationOptionSchema = z.object({
  id: z.guid(),
  name: z.string(),
  isDefault: z.boolean().default(false),
  countryDefinitionId: z
    .guid()
    .nullish()
    .transform((v) => v ?? null)
});

/**
 * GET /api/v1/users/my-students/registration-options — the register form's two picker lists.
 *
 * ⚠ Why not the admin queries: `ListSchoolsQuery` needs `identity.schools.view`, which a teacher does not
 * hold, and it returns the whole school record (tax id, contact person, address). This carries id + display
 * name for live, active schools, and the ASSIGNABLE roles only — participant (student, parent …) plus
 * supervisor (teacher). Administrator, superadmin and partner roles are never offered, and the write
 * re-checks the same set.
 */
export const myStudentRegistrationOptionsSchema = z.object({
  schools: z.array(studentRegistrationOptionSchema),
  roles: z.array(studentRegistrationOptionSchema)
});

export type StudentRegistrationOption = z.infer<typeof studentRegistrationOptionSchema>;
export type MyStudentRegistrationOptions = z.infer<typeof myStudentRegistrationOptionsSchema>;

export const inviteMyStudentResultSchema = z.object({
  userId: z.guid(),
  displayName: z.string(),
  status: z.string(),
  /** created = a new account · linked = the address already had one. */
  outcome: z.enum(['created', 'linked']),
  /** STU-HANDLE: the generated sign-in handle (f.l.15), present only when no e-mail was given. */
  username: z.string().nullish(),
  /**
   * STU-HANDLE: the one-time password, present only on the create-without-e-mail path.
   *
   * ⚠ THIS IS THE ONLY TIME IT EXISTS IN READABLE FORM. It is hashed server-side before the row is written,
   * so it cannot be re-read — only re-issued. The UI must show it once and say so; putting it in a toast that
   * auto-dismisses over a credential nobody has written down is a support ticket.
   */
  temporaryPassword: z.string().nullish()
});

/**
 * STU-CRED — GET /api/v1/users/my-students/{id}: how ONE of my students signs in.
 *
 * Feeds the Details box on the teacher's My-invitations tab, which answers the question a teacher actually
 * asks the lesson after an invite: "this student is standing here and cannot log in."
 */
export const myStudentCredentialsSchema = z.object({
  userId: z.guid(),
  displayName: z.string(),
  /** Null for a student created without one (mig 314 made the column nullable). */
  email: z.string().nullish(),
  /** The generated handle (a.l.15) or one the student chose. Null = e-mail-only sign-in. */
  username: z.string().nullish(),
  hasEmail: z.boolean(),
  /**
   * ⚠ THE field that decides whether a re-issue is offered — not `status`, which is 'active' for a handle
   * student from the moment they are created. Once this is true the student can reset their own password, and
   * the server REFUSES a teacher-issued one with a 409.
   */
  emailVerified: z.boolean(),
  /** Still on a teacher-issued credential, i.e. they have not signed in and chosen their own yet. */
  mustChangePassword: z.boolean()
});

/**
 * STU-CRED — POST /api/v1/users/my-students/{id}/reset-password.
 *
 * ⚠ This is a RE-ISSUE, not a reveal, and no endpoint can ever be the latter: the password is Argon2id-hashed
 * at rest and the plaintext is never stored. So `temporaryPassword` is a BRAND-NEW value and the previous one
 * stops working the instant this returns — the UI has to say so, or a teacher will keep handing out the old
 * slip. Like the invite response, this is the only time it exists in readable form.
 */
export const myStudentPasswordResetSchema = z.object({
  userId: z.guid(),
  displayName: z.string(),
  /** TCH-USR: may be a handle this very call minted — see `usernameCreated`. */
  username: z.string(),
  temporaryPassword: z.string(),
  /**
   * TCH-USR: the student had NO username and one was created along with the password. Two consequences the
   * UI must state, because neither is guessable: the student now signs in with a handle they have never
   * seen, and any activation link already sent to them is dead (activation refuses an account that has a
   * password). Defaulted so an older server's response still parses.
   */
  usernameCreated: z.boolean().nullish().transform((v) => v ?? false)
});

/**
 * PROF-REQ — the profile fields a user is expected to have filled in, as the server names them.
 *
 * ⚠ THESE ARE WIRE NAMES, NOT LABELS, and they match the form controls on purpose: the server returns
 * `missingProfileFields` as this exact vocabulary (Identity's `ProfileCompleteness`), so a client can map a
 * missing field straight onto the input that fixes it without a lookup table. Never translate one of these
 * strings — resolve it to a message id at render time.
 */
export const profileCompletenessFields = [
  'firstName',
  'lastName',
  'birthDate',
  'genderDefinitionId',
  'countryDefinitionId'
] as const;

/**
 * TCH-USR — one row of GET /api/v1/users/my-students: a student the CALLER supervises.
 *
 * ⚠ NOT the guardian endpoint. `/users/me/students` is a parent's linked children and a different
 * population entirely; this is the teacher/partner-employee roster (Identity's `SupervisedStudentScope`).
 *
 * ⚠ THE ROW CARRIES FIELD NAMES, NEVER DEMOGRAPHIC VALUES. There is no birth date, gender or country here
 * and none may be added: the teacher is entitled to know WHAT a student still has to fill in — the answers
 * are the student's. Same boundary that masks profile-sourced answers on the event roster.
 */
export const myStudentRowSchema = z.object({
  userId: z.guid(),
  displayName: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  /** Null for a student created without one (STU-HANDLE, mig 314). */
  email: z.string().nullish(),
  /** The generated handle (a.l.15) or one the student chose. Null = e-mail-only sign-in. */
  username: z.string().nullish(),
  hasEmail: z.boolean(),
  emailVerified: z.boolean(),
  /** Still on a teacher-issued credential — they have not signed in and chosen their own yet. */
  mustChangePassword: z.boolean(),
  /** Account lifecycle: 'invited' = never activated, 'active', 'disabled'. */
  status: z.string(),
  createdAt: z.string(),
  /**
   * Wire names from {@link profileCompletenessFields}; empty = complete. Kept as a plain string array
   * rather than an enum so a server that adds a field to the rule does not fail the parse and blank the
   * whole page — an unknown name simply renders as itself.
   */
  missingProfileFields: z.array(z.string()).nullish().transform((v) => v ?? [])
});

/**
 * TCH-USR — PATCH /api/v1/users/my-students/{id}/profile: FILL IN what the student left empty.
 *
 * ⚠ THIS IS NOT THE NULL-THROUGH CONTRACT of PUT /users/me/profile, where a null field CLEARS the
 * attribute. Here an omitted field is LEFT ALONE, and a field the student has already answered is
 * REFUSED with a 409 (`teacher-student-profile-field-answered`, whose detail names the fields) rather
 * than overwritten. The data subject's own answer always wins.
 */
export interface FillMyStudentProfileRequest {
  firstName?: string;
  lastName?: string;
  /** ISO date (YYYY-MM-DD). */
  birthDate?: string;
  /** Opaque definition id, group 'gender'. Guid.Empty is rejected by the server — omit instead. */
  genderDefinitionId?: string;
  /** Opaque definition id, group 'country'. */
  countryDefinitionId?: string;
}

/** TCH-USR — POST /api/v1/users/my-students/{id}/resend-activation. */
export const activationResultSchema = z.object({
  status: z.string()
});

// ==================== PPT-TCH — a partner employee assigns one of their teachers ==================== //
//
// ⚠ WHAT THIS WRITES IS AN ACCESS EDGE. `teacher_user_id` is the only source the teacher-scope gRPC edge
// resolves "who are my students" from, so the chosen teacher gains the students' travel details, passport
// presence, event registrations and ticket assignment. The CALLER gains nothing new — they already see those
// students through their company. Plan: ZerEduService/docs/partner-assign-teacher-plan.md.

/** One of the caller's company teachers, for the picker. */
export const partnerTeacherOptionSchema = z.object({
  userId: z.guid(),
  displayName: z.string()
});

/**
 * What assignment WOULD do to a row. Rendered from CODES [STD-I18N] — the server never sends prose.
 *
 * ⚠ ONE "held" case, not two (2026-08-13 decision). An earlier version split "another of our teachers" from
 * "the teacher the student chose" because only the first was movable; the coordinator may now override either,
 * so a distinction that changes nothing would only be UI to explain.
 */
export const teacherAssignOutcomes = [
  'would_assign',
  'already_this_teacher',
  'has_other_teacher'
] as const;

export const teacherAssignCandidateSchema = z.object({
  userId: z.guid(),
  // Parts AND the composed name: re-splitting a display name guesses which token is the middle one, and
  // USR-PARTS exists because that guess was wrong.
  firstName: z.string().nullish(),
  middleName: z.string().nullish(),
  lastName: z.string().nullish(),
  displayName: z.string(),
  email: z.string().nullish(),
  username: z.string().nullish(),
  countryDefinitionId: z.guid().nullish(),
  /** The types this user routes through the caller's company — context for the coordinator, not a write key. */
  programTypeIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  currentTeacherUserId: z.guid().nullish(),
  /** Who holds them now — what makes a "will be skipped/moved" row explicable. */
  currentTeacherName: z.string().nullish(),
  /** Kept a plain string: an unrecognised code from a newer server must still render. */
  outcomeIfAssigned: z.string()
});

export const teacherAssignCandidateListSchema = z.object({
  items: z.array(teacherAssignCandidateSchema).nullish().transform((v) => v ?? []),
  totalItems: z.number().int(),
  /**
   * Counts over the WHOLE filtered set, not the page: the confirmation has to say how many people move, and
   * a page-scoped count cannot see the ones the coordinator never scrolled to.
   */
  wouldAssignCount: z.number().int(),
  alreadyThisTeacherCount: z.number().int(),
  hasOtherTeacherCount: z.number().int()
});

/** `truncated` = the server returned fewer ids than match, so "select all" must SAY it selected fewer. */
export const teacherAssignCandidateIdsSchema = z.object({
  userIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  truncated: z.boolean()
});

export const teacherAssignRowSchema = z.object({
  userId: z.guid(),
  outcome: z.string(),
  reason: z.string().nullish(),
  previousTeacherName: z.string().nullish()
});

export const teacherAssignResultSchema = z.object({
  teacherUserId: z.guid(),
  rows: z.array(teacherAssignRowSchema).nullish().transform((v) => v ?? []),
  assignedCount: z.number().int(),
  replacedCount: z.number().int(),
  unchangedCount: z.number().int(),
  skippedCount: z.number().int()
});

/**
 * POST /api/v1/users/my-partner-students/assign-teacher.
 *
 * ⚠ NO companyId — the server resolves it from the caller's own employment link. Sending one would let any
 * holder of the permission route a rival company's students.
 */
export interface AssignPartnerTeacherRequest {
  teacherUserId: string;
  userIds: string[];
  /**
   * Moves students who already have a teacher — ANY teacher, including one the student chose themselves.
   * Unticked, those rows are skipped and reported with the holder's name.
   */
  replaceExisting?: boolean;
}

export type PartnerTeacherOption = z.infer<typeof partnerTeacherOptionSchema>;
export type TeacherAssignCandidate = z.infer<typeof teacherAssignCandidateSchema>;
export type TeacherAssignCandidateList = z.infer<typeof teacherAssignCandidateListSchema>;
export type TeacherAssignResult = z.infer<typeof teacherAssignResultSchema>;
export type TeacherAssignRow = z.infer<typeof teacherAssignRowSchema>;

// ============================ TCH-XLSX — bulk register from a spreadsheet ====================== //

/**
 * The row-error codes the server reports. Mirrors `BulkRegisterErrorCodes` on the Identity side.
 *
 * ⚠ CODES, NEVER PROSE, and that is not a style preference: the report is rendered in the reader's
 * language, so an English sentence from the server would be untranslatable. Each code maps to a message
 * id in the SPA.
 */
export const bulkRegisterErrorCodes = [
  'first_name_required',
  'last_name_required',
  'name_too_long',
  'email_invalid',
  'email_too_long',
  'email_duplicate_in_file',
  'email_already_registered',
  'email_belongs_to_deleted_account',
  'gender_unknown',
  'birth_date_invalid',
  'school_unknown',
  'role_unknown',
  /**
   * Two LIVE rows share the name the cell holds. Neither table is unique on its display name, so this is
   * reachable from ordinary tenant data — and resolving it by picking one would put a student in a school
   * they do not attend, or grant a role nobody chose.
   */
  'school_ambiguous',
  'role_ambiguous',
  /**
   * TCH-XLSX-SCOPE: the upload was scoped to ONE school and this row names a different one. Distinct from
   * `school_unknown` on purpose — the school is real and live, so "retype the cell" is the wrong advice; the
   * file was built for another school. Never silently corrected: that would move a student without saying so.
   */
  'school_outside_selection',
  /** TCH-XLSX-SCOPE: scoped to a COUNTRY, and this row's school is in another one — or in none at all. */
  'school_outside_country'
  // ⚠ STILL NO 'country_unknown': the Country COLUMN is informational and never read. The country a created
  // account gets comes from the scope the dialog sends, falling back to the uploader's own.
] as const;

/** Whole-FILE rejections (as opposed to row ones): unreadable archive, wrong shape, too many rows. */
export const bulkRegisterFileErrors = [
  'file_unreadable',
  'file_no_sheet',
  'file_no_rows',
  'file_too_many_rows',
  'file_not_template'
] as const;

/**
 * One rejected row.
 *
 * ⚠ `row` is the number AS IT APPEARS IN EXCEL, header included. It is the whole usable output of a
 * rejected upload — a 200-row file whose errors carry no row numbers cannot be fixed.
 */
export const bulkRegisterRowErrorSchema = z.object({
  row: z.number(),
  column: z.string(),
  /** Kept a plain string, not the enum: an unrecognised code from a newer server must still render. */
  code: z.string(),
  /** The offending cell text, echoed back so the teacher can find it without opening the file alongside. */
  value: z.string().nullish()
});

export const bulkRegisterRowErrorListSchema = z.array(bulkRegisterRowErrorSchema);

/** One created account. */
export const bulkRegisteredStudentSchema = z.object({
  userId: z.guid(),
  displayName: z.string(),
  email: z.string().nullish(),
  username: z.string().nullish(),
  /**
   * ⚠ PRESENT ONLY for a row with no e-mail, and the ONLY time this value exists in readable form — it is
   * hashed before the row is written, so it can never be re-read, only re-issued from the Manage box. A
   * 50-row file returns 50 of these: the UI must offer to SAVE them, never merely display them.
   */
  temporaryPassword: z.string().nullish()
});

export const bulkRegisterResultSchema = z.object({
  createdCount: z.number(),
  students: z.array(bulkRegisteredStudentSchema)
});

/**
 * A gender the CLIENT resolved from the Definitions catalogue, sent WITH the upload and with the template
 * request.
 *
 * ⚠ Identity cannot read definitions.definitions at all [STD-OWN] — there is no channel to that service —
 * so it cannot turn "Kadın" into an id on its own. The same trust as `PUT /users/me/profile`, which already
 * accepts an arbitrary GUID for that field for exactly this reason.
 *
 * ⚠ SCHOOLS AND ROLES ARE NOT SENT. They live in Identity's own tables, so the server builds those two
 * dropdowns and resolves those two columns itself — nothing the client sends decides which school or role a
 * row may name.
 */
export interface BulkRegisterChoice {
  id: string;
  name: string;
}

/**
 * The template's visible words. Passed in so the workbook is generated in the READER's language — copy
 * lives in the SPA's catalogues, and a hard-coded Turkish header would be the one part of this feature a
 * German teacher could not use.
 */
export interface RosterTemplateLabels {
  sheetName: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  birthDate: string;
  gender: string;
  school: string;
  role: string;
  country: string;
  instructions: string;
  /**
   * The note on the Country header, which must already NAME the uploader's country. That column does
   * nothing on upload, so this sentence is the only thing standing between the teacher and "why did my
   * Country column get ignored".
   */
  countryNote: string;
}

/**
 * TCH-XLSX-SCOPE: what the teacher narrowed the template to.
 *
 * ⚠ THE SAME OBJECT MUST BE SENT WITH THE UPLOAD. The template request alone is a promise the upload would
 * not keep: a file scoped to one school, plus a pasted row naming another, would import.
 *
 * `countryName` is cosmetic — the cell text — because Identity cannot resolve a definitions id to a name
 * [STD-OWN]. `schoolId` is checked against Identity's own table, and against `countryDefinitionId`.
 */
export interface BulkRegisterScope {
  countryDefinitionId?: string | null;
  countryName?: string | null;
  schoolId?: string | null;
}

export interface BulkRegisterTemplateRequest extends BulkRegisterScope {
  genders: BulkRegisterChoice[];
  labels: RosterTemplateLabels;
}

/**
 * The `references` multipart part — the catalogue the server matches cell text against, plus the scope it
 * enforces. `countryName` is not needed here (nothing is written into a cell), so it is omitted.
 */
export interface BulkRegisterReferences {
  genders: BulkRegisterChoice[];
  countryDefinitionId?: string | null;
  schoolId?: string | null;
}

/**
 * Reads the row report out of a 422 problem.
 *
 * ⚠ It travels in the problem's EXTENSIONS (`rowErrors`), which survive because ProblemDetails carries an
 * index signature and the client does not zod-strip an error body. Returns an empty array for any other
 * failure, so a caller can render "the whole file was rejected" without special-casing shapes.
 */
export function bulkRegisterRowErrors(problem: ProblemDetails): BulkRegisterRowError[] {
  const parsed = bulkRegisterRowErrorListSchema.safeParse(problem.rowErrors);
  return parsed.success ? parsed.data : [];
}

/** Reads the whole-file rejection code out of a 422 problem, when that is what happened. */
export function bulkRegisterFileError(problem: ProblemDetails): string | null {
  return typeof problem.fileError === 'string' ? problem.fileError : null;
}

export interface PurchaseTicketBatchRequest {
  ticketId: string;
  quantity: number;
}

export interface AssignTicketRequest {
  studentUserRef: string;
}

/**
 * TCH-BULK — POST /events/{eventId}/teacher/ticket-batches/{batchId}/assign.
 *
 * ⚠ NO SEAT IDS. The server picks which free seat each student gets (creation order): letting the client
 * name seats would mean trusting that they were still free when it looked, which two teachers distributing
 * at once turns into an overwrite.
 */
export interface BulkAssignTicketsRequest {
  studentUserRefs: string[];
}

/** Why a student was skipped. Anything unrecognised renders as a generic "could not assign". */
export const bulkAssignSkipReasons = [
  'already_registered',
  'already_holds_ticket',
  'out_of_scope',
  'no_seat_left'
] as const;

export const bulkAssignOutcomeSchema = z.object({
  studentUserRef: z.guid(),
  studentName: z.string(),
  assigned: z.boolean(),
  /** Kept a plain string, not an enum: a server that adds a reason must not fail the parse and blank the
   *  whole result view — the row falls back to generic copy instead. */
  reason: z.string().nullish(),
  allocationId: z.guid().nullish()
});

/**
 * BATCH-CANCEL (mig 464) — POST /events/{eventId}/teacher/ticket-batches/{batchId}/cancel.
 *
 * ⚠ NOT the batch DTO. The server deliberately answers with the two COUNTS instead, because they
 * describe a MOMENT that no refetch can recover: how many seats went back, and how many of those a
 * waiting-list student took immediately. The second number is the difference between "you can change
 * your mind" and "that seat is gone now", and the confirmation is the only place it can be said.
 */
export const ticketBatchCancelResultSchema = z.object({
  batchId: z.guid(),
  /** Always 'cancelled'; sent so the client never assumes the resulting state. */
  paymentState: z.string(),
  releasedSeats: z.number().int(),
  promotedFromWaitlist: z.number().int()
});

export const bulkAssignResultSchema = z.object({
  outcomes: z.array(bulkAssignOutcomeSchema),
  assignedCount: z.number(),
  skippedCount: z.number(),
  /** Seats still free on this order AFTER the write, so the caller need not refetch to show a live cap. */
  remainingSeats: z.number()
});

export type EventRegistrationSource = (typeof eventRegistrationSources)[number];
export type TeacherAnswer = z.infer<typeof teacherAnswerSchema>;
export type TeacherRegistrationRow = z.infer<typeof teacherRegistrationRowSchema>;
export type TeacherTravelRow = z.infer<typeof teacherTravelRowSchema>;
export type TeacherVisaRow = z.infer<typeof teacherVisaRowSchema>;
export type TicketAllocation = z.infer<typeof ticketAllocationSchema>;
export type TicketAllocationStatus = (typeof ticketAllocationStatuses)[number];
export type TicketBatch = z.infer<typeof ticketBatchSchema>;
export type TicketBatchAdmin = z.infer<typeof ticketBatchAdminSchema>;
export type TicketBatchPaymentState = (typeof ticketBatchPaymentStates)[number];
export type TeacherTickets = z.infer<typeof teacherTicketsSchema>;
export type AssignableStudent = z.infer<typeof assignableStudentSchema>;
export type BulkAssignOutcome = z.infer<typeof bulkAssignOutcomeSchema>;
export type BulkAssignResult = z.infer<typeof bulkAssignResultSchema>;
export type TicketBatchCancelResult = z.infer<typeof ticketBatchCancelResultSchema>;
export type BulkAssignSkipReason = (typeof bulkAssignSkipReasons)[number];
export type EventInvitation = z.infer<typeof eventInvitationSchema>;
export type EventInvitationKind = (typeof eventInvitationKinds)[number];
export type EventInvitationStatus = (typeof eventInvitationStatuses)[number];
export type EventInvitationOutcome = z.infer<typeof eventInvitationOutcomeSchema>;
export type InviteStudentsResult = z.infer<typeof inviteStudentsResultSchema>;
export type InviteMyStudentResult = z.infer<typeof inviteMyStudentResultSchema>;
export type MyStudentCredentials = z.infer<typeof myStudentCredentialsSchema>;
export type MyStudentPasswordReset = z.infer<typeof myStudentPasswordResetSchema>;
export type MyStudentRow = z.infer<typeof myStudentRowSchema>;
export type ProfileCompletenessField = (typeof profileCompletenessFields)[number];
export type ActivationResult = z.infer<typeof activationResultSchema>;
export type BulkRegisterRowError = z.infer<typeof bulkRegisterRowErrorSchema>;
export type BulkRegisteredStudent = z.infer<typeof bulkRegisteredStudentSchema>;
export type BulkRegisterResult = z.infer<typeof bulkRegisterResultSchema>;
export type BulkRegisterErrorCode = (typeof bulkRegisterErrorCodes)[number];
export type BulkRegisterFileError = (typeof bulkRegisterFileErrors)[number];

export type EventSummary = z.infer<typeof eventSummarySchema>;
export type EventDetail = z.infer<typeof eventDetailSchema>;
export type EventRegistration = z.infer<typeof eventRegistrationSchema>;

// ------------------------------ C-13b: event sponsors --------------------------------
// DG-3 (mig 205): tier ARTIK OPAK definition id (guid, nullish) — eski sabit string enum'un yerini aldı.
// Değerler Definitions 'sponsor_tiers' grubundan gelir; FE id'ye göre saklar/gösterir [STD-OWN].
export const sponsorContributionTotalSchema = z.object({ currency: z.string(), total: z.number() });

/** SponsorDto — one event sponsor; contactRef opaque (identity id) [STD-OWN]. */
export const sponsorSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  name: z.string(),
  tierDefinitionId: z.guid().nullish(), // opak definition id (sponsor_tiers) — DG-3 mig 205
  contactRef: z.guid().nullish(),
  website: z.string().nullish(),
  contributionAmount: z.number().nullish(),
  currency: z.string().nullish(),
  logoRef: z.string().nullish(),
  notes: z.string().nullish(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate
});
export const sponsorListSchema = z.object({
  items: z.array(sponsorSchema),
  totals: z.array(sponsorContributionTotalSchema)
});
export type Sponsor = z.infer<typeof sponsorSchema>;
export type SponsorContributionTotal = z.infer<typeof sponsorContributionTotalSchema>;

/** POST/PUT sponsor body — a contribution amount requires a 3-letter currency (server + DB CHECK). */
export interface SponsorRequest {
  name: string;
  /** DG-3: opaque definition id (sponsor_tiers); null/omitted = no tier. */
  tierDefinitionId?: string | null;
  contactRef?: string | null;
  website?: string | null;
  contributionAmount?: number | null;
  currency?: string | null;
  logoRef?: string | null;
  notes?: string | null;
}

// ------------------------------ C-13b: event safeguarding ----------------------------
export const safeguardingNoteCategories = ['concern', 'disclosure', 'incident', 'action', 'referral'] as const;
export type SafeguardingNoteCategory = (typeof safeguardingNoteCategories)[number];

/** EventSafeguardingDto — assigned lead + consent flag + append-only note count. SENSITIVE (safeguarding.read). */
export const eventSafeguardingSchema = z.object({
  eventId: z.guid(),
  safeguardingLeadRef: z.guid().nullish(),
  consentRequired: z.boolean(),
  noteCount: z.number().int()
});
export const safeguardingNoteSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  category: z.enum(safeguardingNoteCategories),
  note: z.string(),
  createdByRef: z.guid().nullish(),
  createdAtUtc: isoDate
});
export const safeguardingNoteListSchema = z.array(safeguardingNoteSchema);
export type EventSafeguarding = z.infer<typeof eventSafeguardingSchema>;
export type SafeguardingNote = z.infer<typeof safeguardingNoteSchema>;

/** PUT safeguarding body (engagement.safeguarding.manage). */
export interface UpdateEventSafeguardingRequest {
  safeguardingLeadRef?: string | null;
  consentRequired: boolean;
}
/** POST safeguarding note body (append-only; engagement.safeguarding.manage). */
export interface AddSafeguardingNoteRequest {
  category: SafeguardingNoteCategory;
  note: string;
}

/**
 * CreateEventCommand / UpdateEventCommand body (DG-3 mig 205). eventTypeDefinitionId/
 * languageDefinitionId are OPAQUE definition IDs (guid, nullish) from the Definitions groups
 * event_types/languages; tags are OPAQUE definition IDs (group event_tags). The FE submits ids,
 * never resolves them cross-schema [STD-OWN]. responsibleUserRef is an opaque identity uuid.
 * capacity null = unlimited ("limit registrations" is a UI-only bool). is-online + location(venue)
 * + meetingUrl may all coexist (hybrid). UTC in/out; displayZone is a display-only hint.
 */
export interface EventWriteRequest {
  title: string;
  description?: string | null;
  eventTypeDefinitionId?: string | null;
  /** Opaque definition id (group program_types) — REQUIRED. */
  programTypeId: string;
  /**
   * 3-letter ISO-4217 code for the event's price lists. THE PAYMENT PATH USES THIS: it rides the
   * payable event, is snapshotted onto commerce.orders.currency and reaches Stripe. Omit = service
   * default.
   */
  currency?: string;
  /**
   * CUR (mig 286): the OPAQUE definitions id of the PICKED currency (group 'currencies'). Sent
   * TOGETHER with the code because the server cannot resolve definitions [STD-OWN] — it validates the
   * code's FORMAT only and the FE owns set membership. The FE must take the code FROM the picked
   * option so the two can never disagree.
   */
  currencyDefinitionId?: string | null;
  startsAtUtc: string;
  endsAtUtc: string;
  location?: string | null;
  capacity?: number | null;
  displayZone?: string | null;
  languageDefinitionId?: string | null;
  passingRatio?: number | null;
  hasPricelist?: boolean;
  responsibleUserRef?: string | null;
  /** Organizer company uuid (type='organizer'); required on the backend. */
  organizerId?: string | null;
  isOnline?: boolean;
  meetingUrl?: string | null;
  /**
   * EVT-PAY (mig 271): the final installment must fall at least this many days BEFORE the event
   * starts; options that would not fit are never offered to the payer. Omit/null = service default (7).
   */
  paymentDeadlineDays?: number | null;
  /**
   * FUND (mig 353): the share of a ticket's charged unit price that each funded seat consumes, as a
   * percentage (1..100). Omit/null = platform default (20) — and that is NOT the same as sending 20,
   * which records a choice and stops following the default.
   */
  fundingMinPercent?: number | null;
  // DG-3: each tag element is a definition ID (group event_tags).
  tags?: string[];
  // Audience filters (EV audience epic) — all optional; empty array = unrestricted (open to everyone).
  // DG-3: countries/ageIntervals/genders are definition IDs (groups country/age_intervals/genders);
  // schools = company uuids (type='school', UNCHANGED).
  countries?: string[];
  schools?: string[];
  ageIntervals?: string[];
  genders?: string[];
  /**
   * EV-AUD-EXCL (mig 487): the EXCLUDE side of the same dimensions — "open to everyone except these".
   *
   * ⚠ A dimension EITHER includes OR excludes. Sending both `countries` and `excludedCountries`
   * non-empty is a 422; clear one. (Across DIFFERENT dimensions the two modes mix freely — include
   * a country while excluding a school is fine.)
   *
   * ⚠ Semantics differ from the include side for a caller with no value: an include REQUIRES the
   * attribute (no country on the profile → not eligible), an exclude does not (no country → not
   * excluded). So "everyone except Germany" still reaches anonymous visitors and incomplete profiles.
   *
   * No excludedAgeIntervals — the age_interval dimension does not constrain eligibility server-side.
   */
  excludedCountries?: string[];
  excludedSchools?: string[];
  excludedGenders?: string[];
  /**
   * EVT-MODE (mig 308): off | optional | required per information block.
   *
   * ⚠ OMITTED = LEAVE UNCHANGED on update (the server keeps the stored value) and the column default on
   * create. So a caller that does not know about these cannot accidentally reset them — which is also why
   * they must be listed HERE: a field missing from the wire type is silently dropped, and on the server
   * side that exact omission made every mode change appear to succeed while changing nothing.
   */
  travelInfoMode?: EventInfoMode | null;
  visaInfoMode?: EventInfoMode | null;
  passportMode?: EventInfoMode | null;
}

export type CreateEventRequest = EventWriteRequest;
export type UpdateEventRequest = EventWriteRequest;

/**
 * OFR-REQ: POST /events/{id}/teacher/ticket-offers body — a request for n seats.
 *
 * ⚠ NO PRICE (2026-08-17). The buyer asks for seats at the LIST price and the amount is set on the
 * admin side during approval (a chain step holding *Can Create Payment Plan*). The field is gone from
 * this type on purpose rather than left optional: an optional money field on the buyer's request is an
 * invitation to start sending it again, and the server now **refuses** a body that carries one (422
 * `engagement-offer-amount-not-partner-settable`) instead of silently dropping it.
 */
export interface RequestTicketOfferRequest {
  ticketId: string;
  quantity: number;
}

/** OFR-APR: one snapshot step of an offer's chain, plus its decision once made. */
export const eventTicketOfferStepSchema = z.object({
  stepNo: z.number().int(),
  roleCode: z.string(),
  decision: z.enum(['approved', 'rejected']).nullish(),
  decisionNote: z.string().nullish(),
  decidedByName: z.string().nullish(),
  decidedAtUtc: isoDate.nullish()
});

/**
 * The PARTNER's view of their own offer (create + withdraw responses).
 *
 * ⚠ NO CHAIN — no waiting role, no steps. The approval chain is the tenant's INTERNAL sign-off
 * structure and a partner is an outside party: they need to know the offer is pending, approved, or
 * why it was refused, not whose signature is next. The server does not send it on this surface
 * (PartnerOfferDto), so there is nothing here to hide — hiding it only in the UI would leave the
 * value in the JSON.
 */
export const eventTicketOfferSchema = z.object({
  id: z.guid(),
  eventId: z.guid(),
  ticketId: z.guid(),
  batchId: z.guid(),
  quantity: z.number().int(),
  listUnitAmount: z.number(),
  offeredUnitAmount: z.number(),
  currency: z.string(),
  status: z.enum(offerStatuses),
  createdAtUtc: isoDate,
  decidedAtUtc: isoDate.nullish(),
  /** The rejecting step's reason — for the partner, unlike the chain itself. */
  rejectionNote: z.string().nullish()
});

export type EventTicketOfferStep = z.infer<typeof eventTicketOfferStepSchema>;
export type EventTicketOffer = z.infer<typeof eventTicketOfferSchema>;
export type OfferStatus = (typeof offerStatuses)[number];
